import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import {
  RecordingSession,
  TimelineClip,
  DetachedAudioClip,
  VideoFrameOverlay,
  MediaAssetItem,
  OverlayTrackClip,
  AudioTrackClip,
  EditorTimelineTrack
} from '@/types';
import { exportService } from '@/services/exportService';
import { formatDuration, generateId, downloadBlob, audioBufferToWav, formatFileSize } from '@/utils/helpers';
import { getStockMediaPresets, probeMediaFile } from '@/utils/mediaPresets';
import confetti from 'canvas-confetti';
import {
  Scissors,
  Trash2,
  Play,
  Pause,
  RotateCcw,
  Volume2,
  VolumeX,
  Sparkles,
  Smartphone,
  Film,
  Download,
  Plus,
  Minus,
  Check,
  CheckCircle2,
  Layers,
  Music,
  Maximize2,
  Minimize2,
  ZoomIn,
  ZoomOut,
  FolderOpen,
  Split,
  Tv,
  Monitor,
  AlertCircle,
  Upload,
  Image as ImageIcon,
  ChevronDown,
  Clock,
  MoveHorizontal,
  SlidersHorizontal,
  GripVertical,
  Type,
  Scaling,
  Move,
  CornerDownRight,
  GripHorizontal,
  ChevronLeft,
  ChevronRight,
  X,
  Users
} from 'lucide-react';

interface VideoEditorScreenProps {
  session?: RecordingSession | null;
  recordings?: RecordingSession[];
  onSelectSession?: (rec: RecordingSession) => void;
  onUpdateSession: (updated: RecordingSession) => void;
  onProceedToExport: () => void;
  onNavigateToStudio?: () => void;
  onImportFile?: () => void;
  onNavigateToSeparateVideos?: () => void;
}

interface TimelineDragState {
  type: 'move' | 'resize-left' | 'resize-right';
  trackType: 'overlay' | 'music' | 'video' | 'audio-detached';
  clipId: string;
  startX: number;
  initialStartSec: number;
  initialDurationSec: number;
}

interface CanvasTransformState {
  type: 'move' | 'nw' | 'ne' | 'sw' | 'se' | 'n' | 's' | 'w' | 'e';
  clipId: string;
  startX: number;
  startY: number;
  initialXPercent: number;
  initialYPercent: number;
  initialWidthPercent: number;
  initialHeightPercent: number;
}

export const VideoEditorScreen: React.FC<VideoEditorScreenProps> = ({
  session,
  recordings = [],
  onSelectSession,
  onUpdateSession,
  onProceedToExport,
  onNavigateToStudio,
  onImportFile,
  onNavigateToSeparateVideos
}) => {
  const currentSession: RecordingSession | null =
    session || (recordings.length > 0 ? recordings[0] : null);

  const videoRef = useRef<HTMLVideoElement>(null);
  const playerStageRef = useRef<HTMLDivElement>(null);
  const bgMusicAudioRef = useRef<HTMLAudioElement>(null);
  const timelineContainerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Timeline Mouse Dragging Ref
  const draggingRef = useRef<TimelineDragState | null>(null);
  const [isDraggingActive, setIsDraggingActive] = useState<boolean>(false);
  const [dragTooltip, setDragTooltip] = useState<{ text: string; x: number; y: number } | null>(null);

  // Canvas Manual Resize & Move Transform Ref (For manual resizing on preview player)
  const canvasTransformRef = useRef<CanvasTransformState | null>(null);
  const [isCanvasTransforming, setIsCanvasTransforming] = useState<boolean>(false);

  // File Drag-and-Drop States
  const dragCounterRef = useRef<number>(0);
  const [isWindowFileDragging, setIsWindowFileDragging] = useState<boolean>(false);
  const [isMediaDropZoneHovered, setIsMediaDropZoneHovered] = useState<boolean>(false);
  const [timelineDropGuideSec, setTimelineDropGuideSec] = useState<number | null>(null);

  // Clipchamp Resizable Sections State (Panel & Timeline Resizing)
  const [sidebarWidth, setSidebarWidth] = useState<number>(320);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState<boolean>(false);
  const [timelineHeight, setTimelineHeight] = useState<number>(340);
  const [isResizingSidebar, setIsResizingSidebar] = useState<boolean>(false);
  const [isResizingTimeline, setIsResizingTimeline] = useState<boolean>(false);
  const sidebarResizeRef = useRef<{ startX: number; initialW: number } | null>(null);
  const timelineResizeRef = useRef<{ startY: number; initialH: number } | null>(null);

  // Clipchamp Video Compositing Export Engine State
  const [showExportModal, setShowExportModal] = useState<boolean>(false);
  const [exportResolution, setExportResolution] = useState<'1080p' | '720p'>('1080p');
  const [exportStatusText, setExportStatusText] = useState<string>('Ready to composite and export video');
  const [exportPreviewUrl, setExportPreviewUrl] = useState<string | null>(null);
  const [exportedBlobUrl, setExportedBlobUrl] = useState<string | null>(null);
  const exportAbortRef = useRef<boolean>(false);

  // Total session master duration
  const masterDurationSec = currentSession?.durationSec || 60;

  // Master Timeline Clips (Video Track)
  const [timelineClips, setTimelineClips] = useState<TimelineClip[]>([]);
  const [selectedClipId, setSelectedClipId] = useState<string | null>(null);

  // Detached Audio Clips (Detached Voice Track)
  const [detachedAudioClips, setDetachedAudioClips] = useState<DetachedAudioClip[]>([]);
  const [selectedAudioClipId, setSelectedAudioClipId] = useState<string | null>(null);
  const [isAudioDetached, setIsAudioDetached] = useState<boolean>(false);

  // Overlay Track Clips (Images, Logos, Watermarks, Picture-in-Picture, Text)
  const [overlayClips, setOverlayClips] = useState<OverlayTrackClip[]>([]);
  const [selectedOverlayClipId, setSelectedOverlayClipId] = useState<string | null>(null);

  // Background Music & External Audio Track Clips
  const [musicClips, setMusicClips] = useState<AudioTrackClip[]>([]);
  const [selectedMusicClipId, setSelectedMusicClipId] = useState<string | null>(null);

  // Active Timeline Tracks
  const [timelineTracks, setTimelineTracks] = useState<EditorTimelineTrack[]>([
    { id: 'trk-video', name: 'Main Video Track', type: 'video', isVisible: true, isMuted: false },
    { id: 'trk-overlay', name: 'Overlay & Image Track', type: 'overlay', isVisible: true, isMuted: false },
    { id: 'trk-music', name: 'Music & Audio Track', type: 'audio-music', isVisible: true, isMuted: false }
  ]);
  const [showAddTrackMenu, setShowAddTrackMenu] = useState<boolean>(false);

  // Media Library Assets
  const [mediaLibrary, setMediaLibrary] = useState<MediaAssetItem[]>([]);
  const [mediaFilter, setMediaFilter] = useState<'all' | 'video' | 'image' | 'audio'>('all');
  const [isImportingMedia, setIsImportingMedia] = useState<boolean>(false);

  // Frame Overlay & Aspect Ratio
  const [activeFrame, setActiveFrame] = useState<VideoFrameOverlay>('none');
  const [activeAspectRatio, setActiveAspectRatio] = useState<'16:9' | '9:16' | '1:1' | '4:5' | '21:9'>('16:9');

  // Playback & Playhead State
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [playheadSec, setPlayheadSec] = useState<number>(0);
  const [timelineZoom, setTimelineZoom] = useState<number>(18); // Pixels per second
  const [isMuted, setIsMuted] = useState<boolean>(false);
  const [isVideoAudioMuted, setIsVideoAudioMuted] = useState<boolean>(false);
  const [isDownloadingAudio, setIsDownloadingAudio] = useState<boolean>(false);
  const [masterVolume, setMasterVolume] = useState<number>(1.0);

  // Left Sidebar Tabs: media, frames, audio
  const [activeLeftTab, setActiveLeftTab] = useState<'media' | 'frames' | 'audio'>('media');

  // Undo Stack
  const [history, setHistory] = useState<{
    clips: TimelineClip[];
    detachedAudio: DetachedAudioClip[];
    overlays: OverlayTrackClip[];
    music: AudioTrackClip[];
    isAudioDetached: boolean;
    activeFrame: VideoFrameOverlay;
    activeAspectRatio: '16:9' | '9:16' | '1:1' | '4:5' | '21:9';
  }[]>([]);

  // Export & Notification States
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [exportProgress, setExportProgress] = useState<number>(0);
  const [actionMessage, setActionMessage] = useState<string | null>(null);

  // Initialize Media Library
  useEffect(() => {
    const stock = getStockMediaPresets();
    const sessionItems: MediaAssetItem[] = [];

    if (currentSession) {
      sessionItems.push({
        id: currentSession.id,
        name: currentSession.title || 'Master Recording',
        type: 'video',
        url: currentSession.blobUrl || '',
        filePath: currentSession.filePath,
        durationSec: currentSession.durationSec || 60,
        thumbnailUrl: currentSession.thumbnailUrl,
        dimensions: currentSession.resolution
      });
    }

    recordings.forEach(r => {
      if (!sessionItems.some(i => i.id === r.id)) {
        sessionItems.push({
          id: r.id,
          name: r.title,
          type: 'video',
          url: r.blobUrl || '',
          filePath: r.filePath,
          durationSec: r.durationSec,
          thumbnailUrl: r.thumbnailUrl,
          dimensions: r.resolution
        });
      }
    });

    setMediaLibrary([...sessionItems, ...stock]);
  }, [currentSession?.id, recordings]);

  // Initialize Timeline Clips on Session Load
  useEffect(() => {
    if (!currentSession) return;

    const initialClip: TimelineClip = {
      id: generateId('clip'),
      name: currentSession.title ? `${currentSession.title.substring(0, 18)}` : 'Main Video Track',
      sourceStartSec: 0,
      sourceEndSec: masterDurationSec,
      timelineStartSec: 0,
      durationSec: masterDurationSec,
      color: 'bg-indigo-600',
      isMuted: false,
      volume: 1.0,
      thumbnailUrl: currentSession.thumbnailUrl
    };

    setTimelineClips([initialClip]);
    setSelectedClipId(initialClip.id);
    setDetachedAudioClips([]);
    setIsAudioDetached(false);
    setOverlayClips([]);
    setMusicClips([]);
    setPlayheadSec(0);

    if (currentSession.aspectRatio === '9:16') {
      setActiveAspectRatio('9:16');
      setActiveFrame('smartphone_916');
    } else if (currentSession.aspectRatio === '1:1') {
      setActiveAspectRatio('1:1');
    } else {
      setActiveAspectRatio('16:9');
    }
  }, [currentSession?.id, masterDurationSec]);

  // Compute total timeline duration
  const totalTimelineDuration = useMemo(() => {
    let maxDur = 0;

    timelineClips.forEach(c => {
      maxDur = Math.max(maxDur, c.timelineStartSec + c.durationSec);
    });

    overlayClips.forEach(c => {
      maxDur = Math.max(maxDur, c.timelineStartSec + c.durationSec);
    });

    musicClips.forEach(c => {
      maxDur = Math.max(maxDur, c.timelineStartSec + c.durationSec);
    });

    detachedAudioClips.forEach(c => {
      maxDur = Math.max(maxDur, c.timelineStartSec + c.durationSec);
    });

    return Math.max(maxDur, masterDurationSec, 5);
  }, [timelineClips, overlayClips, musicClips, detachedAudioClips, masterDurationSec]);

  // Push current state to undo history
  const pushToHistory = () => {
    setHistory(prev => [
      ...prev.slice(-15),
      {
        clips: timelineClips,
        detachedAudio: detachedAudioClips,
        overlays: overlayClips,
        music: musicClips,
        isAudioDetached,
        activeFrame,
        activeAspectRatio
      }
    ]);
  };

  const handleUndo = () => {
    if (history.length === 0) return;
    const previous = history[history.length - 1];
    setHistory(prev => prev.slice(0, -1));
    setTimelineClips(previous.clips);
    setDetachedAudioClips(previous.detachedAudio);
    setOverlayClips(previous.overlays);
    setMusicClips(previous.music);
    setIsAudioDetached(previous.isAudioDetached);
    setActiveFrame(previous.activeFrame);
    setActiveAspectRatio(previous.activeAspectRatio);
    notifyAction('↩ Undo: Previous timeline state restored');
  };

  const notifyAction = (msg: string) => {
    setActionMessage(msg);
    setTimeout(() => {
      setActionMessage(null);
    }, 4000);
  };

  // Find active video clip info at current playhead
  const activeClipInfo = useMemo(() => {
    for (const c of timelineClips) {
      if (playheadSec >= c.timelineStartSec && playheadSec <= c.timelineStartSec + c.durationSec) {
        const offsetInClip = playheadSec - c.timelineStartSec;
        const sourceTime = c.sourceStartSec + offsetInClip;
        return { clip: c, offsetInClip, sourceTime };
      }
    }
    if (timelineClips.length > 0) {
      const last = timelineClips[timelineClips.length - 1];
      return { clip: last, offsetInClip: last.durationSec, sourceTime: last.sourceEndSec };
    }
    return null;
  }, [timelineClips, playheadSec]);

  // Find active overlay clip at current playhead
  const activeOverlayClip = useMemo(() => {
    return overlayClips.find(
      c => playheadSec >= c.timelineStartSec && playheadSec <= c.timelineStartSec + c.durationSec
    ) || null;
  }, [overlayClips, playheadSec]);

  // Find active background music clip at current playhead
  const activeMusicClip = useMemo(() => {
    return musicClips.find(
      c => playheadSec >= c.timelineStartSec && playheadSec <= c.timelineStartSec + c.durationSec
    ) || null;
  }, [musicClips, playheadSec]);

  // Selected Clip objects for inspector bar
  const selectedOverlay = useMemo(
    () => overlayClips.find(c => c.id === selectedOverlayClipId) || null,
    [overlayClips, selectedOverlayClipId]
  );

  // Synchronize HTML5 video element source time with playhead
  useEffect(() => {
    if (!videoRef.current || !activeClipInfo) return;

    const targetTime = activeClipInfo.sourceTime;
    if (Math.abs(videoRef.current.currentTime - targetTime) > 0.25) {
      videoRef.current.currentTime = targetTime;
    }
  }, [playheadSec, activeClipInfo]);

  // Synchronize Background Music audio element with playhead
  useEffect(() => {
    const audioEl = bgMusicAudioRef.current;
    if (!audioEl) return;

    if (activeMusicClip && !activeMusicClip.isMuted) {
      const targetTime = Math.max(0, playheadSec - activeMusicClip.timelineStartSec);
      if (audioEl.src !== activeMusicClip.url) {
        audioEl.src = activeMusicClip.url;
      }
      if (Math.abs(audioEl.currentTime - targetTime) > 0.3) {
        audioEl.currentTime = targetTime;
      }
      audioEl.volume = Math.min(1, (activeMusicClip.volume ?? 0.8) * masterVolume);
      if (isPlaying && audioEl.paused) {
        audioEl.play().catch(() => {});
      }
    } else {
      if (!audioEl.paused) {
        audioEl.pause();
      }
    }
  }, [playheadSec, activeMusicClip, isPlaying, masterVolume]);

  // Playback transport animation loop
  useEffect(() => {
    let animId: number;
    let lastTimestamp: number | null = null;

    const tick = (timestamp: number) => {
      if (!lastTimestamp) lastTimestamp = timestamp;
      const deltaSec = (timestamp - lastTimestamp) / 1000;
      lastTimestamp = timestamp;

      setPlayheadSec(prev => {
        const next = prev + deltaSec;
        if (next >= totalTimelineDuration) {
          setIsPlaying(false);
          if (videoRef.current) videoRef.current.pause();
          if (bgMusicAudioRef.current) bgMusicAudioRef.current.pause();
          return 0;
        }
        return next;
      });

      if (isPlaying) {
        animId = requestAnimationFrame(tick);
      }
    };

    if (isPlaying) {
      if (videoRef.current && videoRef.current.paused) {
        videoRef.current.play().catch(() => {});
      }
      if (bgMusicAudioRef.current && activeMusicClip && !activeMusicClip.isMuted && bgMusicAudioRef.current.paused) {
        bgMusicAudioRef.current.play().catch(() => {});
      }
      animId = requestAnimationFrame(tick);
    } else {
      if (videoRef.current && !videoRef.current.paused) {
        videoRef.current.pause();
      }
      if (bgMusicAudioRef.current && !bgMusicAudioRef.current.paused) {
        bgMusicAudioRef.current.pause();
      }
    }

    return () => {
      if (animId) cancelAnimationFrame(animId);
    };
  }, [isPlaying, totalTimelineDuration, activeMusicClip]);

  // ============================================================
  // GLOBAL WINDOW DRAG-AND-DROP FILE IMPORT
  // ============================================================
  useEffect(() => {
    const handleWindowDragEnter = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounterRef.current++;
      if (e.dataTransfer && Array.from(e.dataTransfer.types).includes('Files')) {
        setIsWindowFileDragging(true);
      }
    };

    const handleWindowDragLeave = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounterRef.current--;
      if (dragCounterRef.current <= 0) {
        dragCounterRef.current = 0;
        setIsWindowFileDragging(false);
      }
    };

    const handleWindowDragOver = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.dataTransfer) {
        e.dataTransfer.dropEffect = 'copy';
      }
    };

    const handleWindowDrop = (e: DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounterRef.current = 0;
      setIsWindowFileDragging(false);

      if (e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        processDroppedFiles(e.dataTransfer.files, 'library');
      }
    };

    window.addEventListener('dragenter', handleWindowDragEnter);
    window.addEventListener('dragleave', handleWindowDragLeave);
    window.addEventListener('dragover', handleWindowDragOver);
    window.addEventListener('drop', handleWindowDrop);

    return () => {
      window.removeEventListener('dragenter', handleWindowDragEnter);
      window.removeEventListener('dragleave', handleWindowDragLeave);
      window.removeEventListener('dragover', handleWindowDragOver);
      window.removeEventListener('drop', handleWindowDrop);
    };
  }, []);

  const processDroppedFiles = async (
    files: FileList | File[],
    targetLocation: 'library' | 'timeline' | 'canvas',
    targetSec?: number
  ) => {
    if (!files || files.length === 0) return;

    setIsImportingMedia(true);
    notifyAction(`📥 Importing ${files.length} dropped media file(s)...`);

    const newAssets: MediaAssetItem[] = [];
    const validFiles = Array.from(files);

    for (const file of validFiles) {
      try {
        const asset = await probeMediaFile(file);
        newAssets.push(asset);
      } catch (err) {
        console.error('File probe error:', err);
      }
    }

    if (newAssets.length > 0) {
      setMediaLibrary(prev => [...newAssets, ...prev]);

      if (targetLocation === 'timeline' || targetLocation === 'canvas') {
        const placeSec = targetSec !== undefined ? targetSec : playheadSec;
        newAssets.forEach((asset, idx) => {
          const atTime = placeSec + idx * 0.2;
          handleAddAssetToTimelineAtTime(asset, atTime);
        });
        confetti({ particleCount: 75, spread: 65 });
        notifyAction(
          `✓ Success! ${newAssets.length} file(s) dropped & placed directly on timeline at ${formatDuration(placeSec)}!`
        );
      } else {
        confetti({ particleCount: 50, spread: 50 });
        notifyAction(`✓ Success! Added ${newAssets.length} file(s) to Media Library. Click "+ Add to Timeline" to place.`);
      }
    } else {
      notifyAction('⚠️ Koi valid media file nahi mila (.mp4, .png, .jpg, .mp3, etc. drop karein).');
    }

    setIsImportingMedia(false);
  };

  // Helper: Place asset directly at specific timestamp
  const handleAddAssetToTimelineAtTime = (asset: MediaAssetItem, atTime: number) => {
    pushToHistory();
    const cleanTime = Math.max(0, Math.round(atTime * 10) / 10);

    if (asset.type === 'image') {
      const dur = asset.durationSec || 5.0;
      const newOverlay: OverlayTrackClip = {
        id: generateId('ovl'),
        name: asset.name.length > 24 ? `${asset.name.substring(0, 22)}...` : asset.name,
        type: 'image',
        url: asset.url,
        timelineStartSec: cleanTime,
        durationSec: dur,
        position: 'custom',
        xPercent: 68,
        yPercent: 6,
        widthPercent: 26,
        heightPercent: 18,
        opacity: 1.0,
        scale: 1.0
      };

      if (!timelineTracks.some(t => t.type === 'overlay')) {
        setTimelineTracks(prev => [
          ...prev,
          { id: 'trk-overlay', name: 'Overlay & Image Track', type: 'overlay', isVisible: true, isMuted: false }
        ]);
      }

      setOverlayClips(prev => [...prev, newOverlay]);
      setSelectedOverlayClipId(newOverlay.id);
      setSelectedClipId(null);
      setSelectedMusicClipId(null);
    } else if (asset.type === 'audio') {
      const dur = asset.durationSec || 10.0;
      const newMusic: AudioTrackClip = {
        id: generateId('mus'),
        name: asset.name.length > 24 ? `${asset.name.substring(0, 22)}...` : asset.name,
        url: asset.url,
        sourceStartSec: 0,
        sourceEndSec: dur,
        timelineStartSec: cleanTime,
        durationSec: dur,
        volume: 0.8,
        isMuted: false,
        color: 'bg-teal-600'
      };

      if (!timelineTracks.some(t => t.type === 'audio-music')) {
        setTimelineTracks(prev => [
          ...prev,
          { id: 'trk-music', name: 'Music & Audio Track', type: 'audio-music', isVisible: true, isMuted: false }
        ]);
      }

      setMusicClips(prev => [...prev, newMusic]);
      setSelectedMusicClipId(newMusic.id);
      setSelectedClipId(null);
      setSelectedOverlayClipId(null);
    } else if (asset.type === 'video') {
      const newClip: TimelineClip = {
        id: generateId('clip'),
        name: asset.name.length > 20 ? `${asset.name.substring(0, 18)}...` : asset.name,
        sourceStartSec: 0,
        sourceEndSec: asset.durationSec,
        timelineStartSec: cleanTime,
        durationSec: asset.durationSec,
        color: 'bg-indigo-600',
        isMuted: false,
        volume: 1.0,
        thumbnailUrl: asset.thumbnailUrl
      };

      setTimelineClips(prev => [...prev, newClip]);
      setSelectedClipId(newClip.id);
      setSelectedOverlayClipId(null);
      setSelectedMusicClipId(null);
    }
  };

  const handleAddAssetToTimeline = (asset: MediaAssetItem) => {
    handleAddAssetToTimelineAtTime(asset, playheadSec);
    confetti({ particleCount: 60, spread: 50 });
    notifyAction(`✓ Added "${asset.name}" to timeline at ${formatDuration(playheadSec)}!`);
  };

  // Add Custom Text Overlay Action (Clipchamp Style)
  const handleAddTextOverlay = () => {
    pushToHistory();
    const cleanTime = Math.max(0, Math.round(playheadSec * 10) / 10);
    const newTextClip: OverlayTrackClip = {
      id: generateId('txt'),
      name: 'Custom Title Text',
      type: 'text',
      url: '',
      text: 'Title Text Here',
      textColor: '#ffffff',
      fontSize: 28,
      timelineStartSec: cleanTime,
      durationSec: 5.0,
      position: 'custom',
      xPercent: 20,
      yPercent: 40,
      widthPercent: 60,
      heightPercent: 20,
      opacity: 1.0
    };

    if (!timelineTracks.some(t => t.type === 'overlay')) {
      setTimelineTracks(prev => [
        ...prev,
        { id: 'trk-overlay', name: 'Overlay & Image Track', type: 'overlay', isVisible: true, isMuted: false }
      ]);
    }

    setOverlayClips(prev => [...prev, newTextClip]);
    setSelectedOverlayClipId(newTextClip.id);
    setSelectedClipId(null);
    setSelectedMusicClipId(null);
    confetti({ particleCount: 60, spread: 50 });
    notifyAction('✨ Text overlay added to canvas! Drag corner dots to resize or drag to reposition.');
  };

  const handleFilesImported = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      processDroppedFiles(e.target.files, 'library');
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleTimelineDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'copy';

    if (timelineContainerRef.current) {
      const rect = timelineContainerRef.current.getBoundingClientRect();
      const dropX = e.clientX - rect.left + timelineContainerRef.current.scrollLeft;
      const dropSec = Math.max(0, Math.round((dropX / timelineZoom) * 10) / 10);
      setTimelineDropGuideSec(dropSec);
    }
  };

  const handleTimelineDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setTimelineDropGuideSec(null);
  };

  const handleTimelineDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setTimelineDropGuideSec(null);
    setIsWindowFileDragging(false);

    const rect = timelineContainerRef.current?.getBoundingClientRect();
    const dropX = e.clientX - (rect?.left || 0) + (timelineContainerRef.current?.scrollLeft || 0);
    const dropSec = Math.max(0, Math.round((dropX / timelineZoom) * 10) / 10);

    const rawInternal = e.dataTransfer.getData('application/json');
    if (rawInternal) {
      try {
        const parsed = JSON.parse(rawInternal);
        if (parsed.type === 'internal-asset' && parsed.assetId) {
          const found = mediaLibrary.find(m => m.id === parsed.assetId);
          if (found) {
            handleAddAssetToTimelineAtTime(found, dropSec);
            confetti({ particleCount: 60, spread: 50 });
            notifyAction(`✓ Placed "${found.name}" onto timeline at ${formatDuration(dropSec)}!`);
            return;
          }
        }
      } catch (err) {
        console.warn('Internal drag parse error:', err);
      }
    }

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processDroppedFiles(e.dataTransfer.files, 'timeline', dropSec);
    }
  };

  const handleAddNewTrack = (trackType: 'overlay' | 'audio-music' | 'video') => {
    setShowAddTrackMenu(false);
    pushToHistory();

    const trackId = generateId('trk');
    const nameMap = {
      overlay: `Overlay Track ${timelineTracks.filter(t => t.type === 'overlay').length + 1}`,
      'audio-music': `Music Track ${timelineTracks.filter(t => t.type === 'audio-music').length + 1}`,
      video: `Video Track ${timelineTracks.filter(t => t.type === 'video').length + 1}`
    };

    setTimelineTracks(prev => [
      ...prev,
      {
        id: trackId,
        name: nameMap[trackType],
        type: trackType,
        isVisible: true,
        isMuted: false
      }
    ]);

    notifyAction(`✨ New "${nameMap[trackType]}" added to timeline! You can now drop media onto it.`);
  };

  // ============================================================
  // TIMELINE MOUSE DRAG ENGINE: CLIP MOVE & LEFT/RIGHT RESIZE TRIMMING
  // ============================================================
  const handleStartTimelineDrag = (
    e: React.MouseEvent,
    type: 'move' | 'resize-left' | 'resize-right',
    trackType: 'overlay' | 'music' | 'video' | 'audio-detached',
    clipId: string,
    initialStartSec: number,
    initialDurationSec: number
  ) => {
    e.preventDefault();
    e.stopPropagation();

    if (trackType === 'overlay') {
      setSelectedOverlayClipId(clipId);
      setSelectedClipId(null);
      setSelectedMusicClipId(null);
      setSelectedAudioClipId(null);
    } else if (trackType === 'music') {
      setSelectedMusicClipId(clipId);
      setSelectedClipId(null);
      setSelectedOverlayClipId(null);
      setSelectedAudioClipId(null);
    } else if (trackType === 'video') {
      setSelectedClipId(clipId);
      setSelectedOverlayClipId(null);
      setSelectedMusicClipId(null);
      setSelectedAudioClipId(null);
    } else if (trackType === 'audio-detached') {
      setSelectedAudioClipId(clipId);
      setSelectedClipId(null);
      setSelectedOverlayClipId(null);
      setSelectedMusicClipId(null);
    }

    pushToHistory();

    draggingRef.current = {
      type,
      trackType,
      clipId,
      startX: e.clientX,
      initialStartSec,
      initialDurationSec
    };
    setIsDraggingActive(true);
  };

  // ============================================================
  // CANVAS TRANSFORM ENGINE: MANUAL RESIZE & MOVE ON PREVIEW SCREEN
  // (Solves "use manually resize kar sake"!)
  // ============================================================
  const handleStartCanvasTransform = (
    e: React.MouseEvent,
    type: 'move' | 'nw' | 'ne' | 'sw' | 'se' | 'n' | 's' | 'w' | 'e',
    clip: OverlayTrackClip
  ) => {
    e.preventDefault();
    e.stopPropagation();

    setSelectedOverlayClipId(clip.id);
    pushToHistory();

    // Compute active box dimensions in percentages
    let initialX = clip.xPercent ?? 68;
    let initialY = clip.yPercent ?? 6;
    let initialW = clip.widthPercent ?? 26;
    let initialH = clip.heightPercent ?? 18;

    if (clip.position && clip.position !== 'custom') {
      if (clip.position === 'top-left') { initialX = 4; initialY = 4; initialW = 28; initialH = 18; }
      else if (clip.position === 'top-right') { initialX = 68; initialY = 4; initialW = 28; initialH = 18; }
      else if (clip.position === 'bottom-left') { initialX = 4; initialY = 76; initialW = 28; initialH = 18; }
      else if (clip.position === 'bottom-right') { initialX = 68; initialY = 76; initialW = 28; initialH = 18; }
      else if (clip.position === 'center') { initialX = 25; initialY = 25; initialW = 50; initialH = 50; }
      else if (clip.position === 'pip') { initialX = 60; initialY = 62; initialW = 35; initialH = 32; }
    }

    canvasTransformRef.current = {
      type,
      clipId: clip.id,
      startX: e.clientX,
      startY: e.clientY,
      initialXPercent: initialX,
      initialYPercent: initialY,
      initialWidthPercent: initialW,
      initialHeightPercent: initialH
    };
    setIsCanvasTransforming(true);
  };

  // Splitter Handlers: Resizable Panel Width & Resizable Timeline Height
  const handleStartSidebarResize = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    sidebarResizeRef.current = { startX: e.clientX, initialW: sidebarWidth };
    setIsResizingSidebar(true);
  };

  const handleStartTimelineResize = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    timelineResizeRef.current = { startY: e.clientY, initialH: timelineHeight };
    setIsResizingTimeline(true);
  };

  useEffect(() => {
    const handleGlobalMouseMove = (e: MouseEvent) => {
      // 1. Timeline Dragging
      if (draggingRef.current) {
        const { type, trackType, clipId, startX, initialStartSec, initialDurationSec } = draggingRef.current;
        const deltaPx = e.clientX - startX;
        const deltaSec = deltaPx / timelineZoom;

        if (type === 'move') {
          const newStart = Math.max(0, Math.round((initialStartSec + deltaSec) * 10) / 10);
          setDragTooltip({
            text: `📍 Position: ${formatDuration(newStart)} (${newStart.toFixed(1)}s)`,
            x: e.clientX,
            y: e.clientY - 40
          });

          if (trackType === 'overlay') {
            setOverlayClips(prev =>
              prev.map(c => (c.id === clipId ? { ...c, timelineStartSec: newStart } : c))
            );
          } else if (trackType === 'music') {
            setMusicClips(prev =>
              prev.map(c => (c.id === clipId ? { ...c, timelineStartSec: newStart } : c))
            );
          } else if (trackType === 'video') {
            setTimelineClips(prev =>
              prev.map(c => (c.id === clipId ? { ...c, timelineStartSec: newStart } : c))
            );
          } else if (trackType === 'audio-detached') {
            setDetachedAudioClips(prev =>
              prev.map(c => (c.id === clipId ? { ...c, timelineStartSec: newStart } : c))
            );
          }
        } else if (type === 'resize-right') {
          const newDur = Math.max(0.5, Math.round((initialDurationSec + deltaSec) * 10) / 10);
          const endSec = initialStartSec + newDur;
          setDragTooltip({
            text: `↔️ Duration: ${newDur.toFixed(1)}s (End: ${formatDuration(endSec)})`,
            x: e.clientX,
            y: e.clientY - 40
          });

          if (trackType === 'overlay') {
            setOverlayClips(prev =>
              prev.map(c => (c.id === clipId ? { ...c, durationSec: newDur } : c))
            );
          } else if (trackType === 'music') {
            setMusicClips(prev =>
              prev.map(c =>
                c.id === clipId ? { ...c, durationSec: newDur, sourceEndSec: c.sourceStartSec + newDur } : c
              )
            );
          } else if (trackType === 'video') {
            setTimelineClips(prev =>
              prev.map(c =>
                c.id === clipId ? { ...c, durationSec: newDur, sourceEndSec: c.sourceStartSec + newDur } : c
              )
            );
          }
        } else if (type === 'resize-left') {
          const fixedEnd = initialStartSec + initialDurationSec;
          const proposedStart = Math.max(
            0,
            Math.min(fixedEnd - 0.5, Math.round((initialStartSec + deltaSec) * 10) / 10)
          );
          const newDur = Math.max(0.5, Math.round((fixedEnd - proposedStart) * 10) / 10);

          setDragTooltip({
            text: `✂️ Start: ${formatDuration(proposedStart)} | Duration: ${newDur.toFixed(1)}s`,
            x: e.clientX,
            y: e.clientY - 40
          });

          if (trackType === 'overlay') {
            setOverlayClips(prev =>
              prev.map(c =>
                c.id === clipId ? { ...c, timelineStartSec: proposedStart, durationSec: newDur } : c
              )
            );
          } else if (trackType === 'music') {
            setMusicClips(prev =>
              prev.map(c =>
                c.id === clipId ? { ...c, timelineStartSec: proposedStart, durationSec: newDur } : c
              )
            );
          } else if (trackType === 'video') {
            setTimelineClips(prev =>
              prev.map(c =>
                c.id === clipId ? { ...c, timelineStartSec: proposedStart, durationSec: newDur } : c
              )
            );
          }
        }
        return;
      }

      // 2. Canvas Manual Resize & Move Transform
      if (canvasTransformRef.current && playerStageRef.current) {
        const {
          type,
          clipId,
          startX,
          startY,
          initialXPercent,
          initialYPercent,
          initialWidthPercent,
          initialHeightPercent
        } = canvasTransformRef.current;

        const stageRect = playerStageRef.current.getBoundingClientRect();
        const deltaXPct = ((e.clientX - startX) / stageRect.width) * 100;
        const deltaYPct = ((e.clientY - startY) / stageRect.height) * 100;

        let newX = initialXPercent;
        let newY = initialYPercent;
        let newW = initialWidthPercent;
        let newH = initialHeightPercent;

        if (type === 'move') {
          newX = Math.max(0, Math.min(100 - initialWidthPercent, Math.round(initialXPercent + deltaXPct)));
          newY = Math.max(0, Math.min(100 - initialHeightPercent, Math.round(initialYPercent + deltaYPct)));
        } else if (type === 'se') {
          // Dragging Bottom-Right Corner
          newW = Math.max(8, Math.min(100 - initialXPercent, Math.round(initialWidthPercent + deltaXPct)));
          newH = Math.max(8, Math.min(100 - initialYPercent, Math.round(initialHeightPercent + deltaYPct)));
        } else if (type === 'sw') {
          // Dragging Bottom-Left Corner
          const candidateX = Math.max(0, Math.min(initialXPercent + initialWidthPercent - 8, Math.round(initialXPercent + deltaXPct)));
          newW = Math.round(initialXPercent + initialWidthPercent - candidateX);
          newX = candidateX;
          newH = Math.max(8, Math.min(100 - initialYPercent, Math.round(initialHeightPercent + deltaYPct)));
        } else if (type === 'ne') {
          // Dragging Top-Right Corner
          const candidateY = Math.max(0, Math.min(initialYPercent + initialHeightPercent - 8, Math.round(initialYPercent + deltaYPct)));
          newH = Math.round(initialYPercent + initialHeightPercent - candidateY);
          newY = candidateY;
          newW = Math.max(8, Math.min(100 - initialXPercent, Math.round(initialWidthPercent + deltaXPct)));
        } else if (type === 'nw') {
          // Dragging Top-Left Corner
          const candidateX = Math.max(0, Math.min(initialXPercent + initialWidthPercent - 8, Math.round(initialXPercent + deltaXPct)));
          const candidateY = Math.max(0, Math.min(initialYPercent + initialHeightPercent - 8, Math.round(initialYPercent + deltaYPct)));
          newW = Math.round(initialXPercent + initialWidthPercent - candidateX);
          newH = Math.round(initialYPercent + initialHeightPercent - candidateY);
          newX = candidateX;
          newY = candidateY;
        } else if (type === 'e') {
          newW = Math.max(8, Math.min(100 - initialXPercent, Math.round(initialWidthPercent + deltaXPct)));
        } else if (type === 's') {
          newH = Math.max(8, Math.min(100 - initialYPercent, Math.round(initialHeightPercent + deltaYPct)));
        } else if (type === 'w') {
          const candidateX = Math.max(0, Math.min(initialXPercent + initialWidthPercent - 8, Math.round(initialXPercent + deltaXPct)));
          newW = Math.round(initialXPercent + initialWidthPercent - candidateX);
          newX = candidateX;
        } else if (type === 'n') {
          const candidateY = Math.max(0, Math.min(initialYPercent + initialHeightPercent - 8, Math.round(initialYPercent + deltaYPct)));
          newH = Math.round(initialYPercent + initialHeightPercent - candidateY);
          newY = candidateY;
        }

        setOverlayClips(prev =>
          prev.map(c =>
            c.id === clipId
              ? {
                  ...c,
                  xPercent: newX,
                  yPercent: newY,
                  widthPercent: newW,
                  heightPercent: newH,
                  position: 'custom'
                }
              : c
          )
        );
        return;
      }

      // 3. Resizing Left Sidebar Width
      if (sidebarResizeRef.current) {
        const delta = e.clientX - sidebarResizeRef.current.startX;
        const newW = Math.max(220, Math.min(560, Math.round(sidebarResizeRef.current.initialW + delta)));
        setSidebarWidth(newW);
        return;
      }

      // 4. Resizing Timeline Height (Drag Up expands timeline, Drag Down shrinks)
      if (timelineResizeRef.current) {
        const delta = timelineResizeRef.current.startY - e.clientY;
        const newH = Math.max(180, Math.min(window.innerHeight - 200, Math.round(timelineResizeRef.current.initialH + delta)));
        setTimelineHeight(newH);
        return;
      }
    };

    const handleGlobalMouseUp = () => {
      if (sidebarResizeRef.current) {
        sidebarResizeRef.current = null;
        setIsResizingSidebar(false);
      }
      if (timelineResizeRef.current) {
        timelineResizeRef.current = null;
        setIsResizingTimeline(false);
      }
      if (draggingRef.current) {
        draggingRef.current = null;
        setIsDraggingActive(false);
        setDragTooltip(null);
        notifyAction('✓ Timeline clip updated!');
      }
      if (canvasTransformRef.current) {
        canvasTransformRef.current = null;
        setIsCanvasTransforming(false);
        notifyAction('✓ Overlay manually resized on canvas!');
      }
    };

    window.addEventListener('mousemove', handleGlobalMouseMove);
    window.addEventListener('mouseup', handleGlobalMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleGlobalMouseMove);
      window.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [timelineZoom]);

  // Split, Delete, Mute, Detach
  const handleSplitAtPlayhead = useCallback(() => {
    if (selectedOverlayClipId) {
      const idx = overlayClips.findIndex(c => c.id === selectedOverlayClipId);
      if (idx !== -1) {
        const ovl = overlayClips[idx];
        const offset = playheadSec - ovl.timelineStartSec;
        if (offset > 0.25 && offset < ovl.durationSec - 0.25) {
          pushToHistory();
          const p1: OverlayTrackClip = {
            ...ovl,
            id: generateId('ovl'),
            name: `${ovl.name} (Part 1)`,
            durationSec: offset
          };
          const p2: OverlayTrackClip = {
            ...ovl,
            id: generateId('ovl'),
            name: `${ovl.name} (Part 2)`,
            timelineStartSec: ovl.timelineStartSec + offset,
            durationSec: ovl.durationSec - offset
          };
          const updated = [...overlayClips];
          updated.splice(idx, 1, p1, p2);
          setOverlayClips(updated);
          setSelectedOverlayClipId(p2.id);
          notifyAction(`✂️ Overlay Image Clip split at ${formatDuration(playheadSec)}!`);
          return;
        }
      }
    }

    if (selectedMusicClipId) {
      const idx = musicClips.findIndex(c => c.id === selectedMusicClipId);
      if (idx !== -1) {
        const mus = musicClips[idx];
        const offset = playheadSec - mus.timelineStartSec;
        if (offset > 0.25 && offset < mus.durationSec - 0.25) {
          pushToHistory();
          const m1: AudioTrackClip = {
            ...mus,
            id: generateId('mus'),
            name: `${mus.name} (Part 1)`,
            durationSec: offset,
            sourceEndSec: mus.sourceStartSec + offset
          };
          const m2: AudioTrackClip = {
            ...mus,
            id: generateId('mus'),
            name: `${mus.name} (Part 2)`,
            timelineStartSec: mus.timelineStartSec + offset,
            sourceStartSec: mus.sourceStartSec + offset,
            durationSec: mus.durationSec - offset
          };
          const updated = [...musicClips];
          updated.splice(idx, 1, m1, m2);
          setMusicClips(updated);
          setSelectedMusicClipId(m2.id);
          notifyAction(`✂️ Music Audio Clip split at ${formatDuration(playheadSec)}!`);
          return;
        }
      }
    }

    if (!activeClipInfo || timelineClips.length === 0) return;

    const { clip, offsetInClip, sourceTime } = activeClipInfo;
    if (offsetInClip < 0.25 || offsetInClip > clip.durationSec - 0.25) {
      notifyAction('⚠️ Playhead clip ke edge ke bohot close hai. Thoda aage/peeche seek karein.');
      return;
    }

    pushToHistory();

    const clipIndex = timelineClips.findIndex(c => c.id === clip.id);
    if (clipIndex === -1) return;

    const part1: TimelineClip = {
      ...clip,
      id: generateId('clip'),
      name: `${clip.name.replace(/ \(Part \d+\)/, '')} (Part 1)`,
      sourceStartSec: clip.sourceStartSec,
      sourceEndSec: sourceTime,
      durationSec: offsetInClip
    };

    const part2: TimelineClip = {
      ...clip,
      id: generateId('clip'),
      name: `${clip.name.replace(/ \(Part \d+\)/, '')} (Part 2)`,
      sourceStartSec: sourceTime,
      sourceEndSec: clip.sourceEndSec,
      timelineStartSec: clip.timelineStartSec + offsetInClip,
      durationSec: clip.durationSec - offsetInClip
    };

    const newClips = [...timelineClips];
    newClips.splice(clipIndex, 1, part1, part2);
    setTimelineClips(newClips);
    setSelectedClipId(part2.id);

    notifyAction(`✂️ Video Clip Split at ${formatDuration(playheadSec)}! (Shortcut: 'S')`);
  }, [
    activeClipInfo,
    timelineClips,
    playheadSec,
    selectedOverlayClipId,
    overlayClips,
    selectedMusicClipId,
    musicClips
  ]);

  const handleDeleteSelectedClip = useCallback(() => {
    if (selectedOverlayClipId) {
      pushToHistory();
      setOverlayClips(prev => prev.filter(c => c.id !== selectedOverlayClipId));
      setSelectedOverlayClipId(null);
      notifyAction('🗑️ Overlay clip removed from timeline.');
      return;
    }

    if (selectedMusicClipId) {
      pushToHistory();
      setMusicClips(prev => prev.filter(c => c.id !== selectedMusicClipId));
      setSelectedMusicClipId(null);
      notifyAction('🗑️ Music audio clip removed from timeline.');
      return;
    }

    if (selectedAudioClipId && detachedAudioClips.length > 0) {
      pushToHistory();
      setDetachedAudioClips(prev => prev.filter(a => a.id !== selectedAudioClipId));
      setSelectedAudioClipId(null);
      notifyAction('🗑️ Audio track segment deleted.');
      return;
    }

    if (selectedClipId && timelineClips.length > 1) {
      pushToHistory();
      const filtered = timelineClips.filter(c => c.id !== selectedClipId);
      setTimelineClips(filtered);
      setSelectedClipId(filtered[0]?.id || null);
      notifyAction('🗑️ Video clip deleted from timeline.');
      return;
    }
  }, [
    selectedClipId,
    selectedAudioClipId,
    selectedOverlayClipId,
    selectedMusicClipId,
    timelineClips,
    detachedAudioClips
  ]);

  const handleToggleDetachAudio = () => {
    pushToHistory();

    if (!isAudioDetached) {
      const newAudios: DetachedAudioClip[] = timelineClips.map((c) => ({
        id: generateId('aud'),
        name: `${c.name} — Audio`,
        sourceStartSec: c.sourceStartSec,
        sourceEndSec: c.sourceEndSec,
        timelineStartSec: c.timelineStartSec,
        durationSec: c.durationSec,
        volume: 1.0,
        isMuted: false,
        color: 'bg-emerald-600'
      }));

      const updatedClips = timelineClips.map(c => ({ ...c, isMuted: true }));
      setTimelineClips(updatedClips);
      setDetachedAudioClips(newAudios);
      setIsAudioDetached(true);

      if (!timelineTracks.some(t => t.type === 'audio-detached')) {
        setTimelineTracks(prev => [
          ...prev,
          { id: 'trk-detached', name: 'Detached Audio Track', type: 'audio-detached', isVisible: true, isMuted: false }
        ]);
      }

      notifyAction('🎵 Audio Detached! Audio ab video se alag track me convert ho chuka hai.');
    } else {
      const updatedClips = timelineClips.map(c => ({ ...c, isMuted: false }));
      setTimelineClips(updatedClips);
      setDetachedAudioClips([]);
      setIsAudioDetached(false);
      notifyAction('🔗 Audio re-attached to video.');
    }
  };

  const handleDownloadAudioOnly = async () => {
    if (!currentSession) return;
    setIsDownloadingAudio(true);
    notifyAction('⏳ Audio extract ho raha hai, please wait...');

    try {
      const safeTitle = (currentSession.title || 'Session_Audio').replace(/[^a-zA-Z0-9_-]/g, '_');

      if (window.electronAPI && currentSession.filePath && typeof window.electronAPI.extractAudio === 'function') {
        const outPath = `C:\\Recordings\\MeetSplitAI\\Exports\\${safeTitle}_Audio.mp3`;
        await window.electronAPI.extractAudio({
          inputPath: currentSession.filePath,
          outputPath: outPath
        });
        if (typeof window.electronAPI.showItemInFolder === 'function') {
          window.electronAPI.showItemInFolder(outPath);
        }
        confetti({ particleCount: 70, spread: 60 });
        notifyAction(`✓ Success! Audio MP3 file export ho chuki hai: ${safeTitle}_Audio.mp3`);
      } else if (currentSession.blobUrl) {
        const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
        const res = await fetch(currentSession.blobUrl);
        const arrayBuffer = await res.arrayBuffer();
        const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer);
        const wavBlob = audioBufferToWav(audioBuffer);
        downloadBlob(wavBlob, `${safeTitle}_Audio.wav`);
        confetti({ particleCount: 70, spread: 60 });
        notifyAction(`✓ Success! Extracted audio downloaded as "${safeTitle}_Audio.wav"!`);
      }
    } catch (e) {
      console.error('Audio extraction error:', e);
      notifyAction('⚠️ Audio downloaded with fallback.');
    } finally {
      setIsDownloadingAudio(false);
    }
  };

  const handleToggleMuteVideoTrack = () => {
    pushToHistory();
    const nextMuted = !isVideoAudioMuted;
    setIsVideoAudioMuted(nextMuted);

    const updated = timelineClips.map(c => ({ ...c, isMuted: nextMuted }));
    setTimelineClips(updated);

    if (videoRef.current) {
      videoRef.current.muted = nextMuted || isMuted;
    }

    notifyAction(nextMuted ? '🔇 Video Audio Muted!' : '🔊 Video Audio Unmuted!');
  };

  const handleToggleClipMute = (clipId: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    pushToHistory();
    const updated = timelineClips.map(c => {
      if (c.id === clipId) return { ...c, isMuted: !c.isMuted };
      return c;
    });
    setTimelineClips(updated);
    notifyAction('🔊 Clip audio setting updated.');
  };

  // Keyboard Shortcuts Hook
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes((e.target as HTMLElement)?.tagName)) {
        return;
      }

      if (e.key === 's' || e.key === 'S') {
        e.preventDefault();
        handleSplitAtPlayhead();
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        e.preventDefault();
        handleDeleteSelectedClip();
      } else if (e.code === 'Space') {
        e.preventDefault();
        setIsPlaying(prev => !prev);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setPlayheadSec(prev => Math.max(0, prev - (e.shiftKey ? 5 : 1)));
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        setPlayheadSec(prev => Math.min(totalTimelineDuration, prev + (e.shiftKey ? 5 : 1)));
      } else if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        handleUndo();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleSplitAtPlayhead, handleDeleteSelectedClip, totalTimelineDuration]);

  const handleTimelineScrub = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isDraggingActive || draggingRef.current) return;
    if (!timelineContainerRef.current) return;
    const rect = timelineContainerRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left + timelineContainerRef.current.scrollLeft;
    const newSec = Math.max(0, Math.min(totalTimelineDuration, clickX / timelineZoom));
    setPlayheadSec(newSec);
  };

  const handleCancelExport = () => {
    exportAbortRef.current = true;
    setIsExporting(false);
    setExportStatusText('Export cancelled.');
    notifyAction('Export cancelled.');
  };

  const handleStartCompositedExport = async () => {
    if (!currentSession) return;
    setIsExporting(true);
    setExportProgress(0);
    setExportStatusText('Initializing composition engine & preloading assets...');
    exportAbortRef.current = false;
    setExportedBlobUrl(null);

    try {
      // 1. Target canvas dimensions based on resolution & aspect ratio
      const is1080 = exportResolution === '1080p';
      let targetW = 1920;
      let targetH = 1080;

      if (activeAspectRatio === '9:16') {
        targetW = is1080 ? 1080 : 720;
        targetH = is1080 ? 1920 : 1280;
      } else if (activeAspectRatio === '1:1') {
        targetW = is1080 ? 1080 : 720;
        targetH = is1080 ? 1080 : 720;
      } else if (activeAspectRatio === '4:5') {
        targetW = is1080 ? 1080 : 720;
        targetH = is1080 ? 1350 : 900;
      } else if (activeAspectRatio === '21:9') {
        targetW = is1080 ? 2560 : 1680;
        targetH = is1080 ? 1080 : 720;
      } else {
        targetW = is1080 ? 1920 : 1280;
        targetH = is1080 ? 1080 : 720;
      }

      // 2. Preload all overlay images
      setExportStatusText('Preloading overlay graphics & media assets...');
      const preloadedImages = new Map<string, HTMLImageElement>();
      await Promise.all(
        overlayClips.filter(c => c.type !== 'text').map(async (ovl) => {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.src = ovl.url;
          await new Promise((resolve) => {
            img.onload = resolve;
            img.onerror = resolve;
          });
          preloadedImages.set(ovl.id, img);
        })
      );

      // 3. Create rendering canvas
      const canvas = document.createElement('canvas');
      canvas.width = targetW;
      canvas.height = targetH;
      const ctx = canvas.getContext('2d', { alpha: false });
      if (!ctx) throw new Error('Could not initialize canvas context');

      // 4. Create offscreen video element
      setExportStatusText('Syncing video master stream...');
      const renderVideo = document.createElement('video');
      renderVideo.src = currentSession.blobUrl || '';
      renderVideo.crossOrigin = 'anonymous';
      renderVideo.preload = 'auto';
      renderVideo.playsInline = true;

      await new Promise<void>((resolve) => {
        if (renderVideo.readyState >= 2) {
          resolve();
        } else {
          renderVideo.onloadeddata = () => resolve();
          renderVideo.onerror = () => resolve();
          setTimeout(resolve, 3000);
        }
      });

      // 5. Setup Web Audio API mixer (routes audio into recorded stream without speaker echo)
      const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
      const dest = audioCtx.createMediaStreamDestination();

      if (!isVideoAudioMuted && !isMuted) {
        try {
          const source = audioCtx.createMediaElementSource(renderVideo);
          source.connect(dest);
        } catch (err) {
          console.warn('Audio capture note:', err);
        }
      }

      // 6. Setup MediaRecorder
      const canvasStream = canvas.captureStream(30);
      const combinedTracks = [
        ...canvasStream.getVideoTracks(),
        ...dest.stream.getAudioTracks()
      ];
      const combinedStream = new MediaStream(combinedTracks);

      let mimeType = 'video/webm;codecs=vp9,opus';
      if (!MediaRecorder.isTypeSupported(mimeType)) mimeType = 'video/webm;codecs=vp8,opus';
      if (!MediaRecorder.isTypeSupported(mimeType)) mimeType = 'video/webm';
      if (!MediaRecorder.isTypeSupported(mimeType) && MediaRecorder.isTypeSupported('video/mp4')) mimeType = 'video/mp4';

      const recorder = new MediaRecorder(combinedStream, {
        mimeType,
        videoBitsPerSecond: is1080 ? 8500000 : 4500000
      });

      const chunks: Blob[] = [];
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data);
      };

      // 7. Render & Recording Loop
      renderVideo.currentTime = 0;
      await renderVideo.play();
      recorder.start(250);

      const totalSec = Math.max(1, totalTimelineDuration);
      let frameCount = 0;

      await new Promise<void>((resolve, reject) => {
        const renderLoop = () => {
          if (exportAbortRef.current) {
            renderVideo.pause();
            if (recorder.state !== 'inactive') recorder.stop();
            audioCtx.close();
            reject(new Error('Export cancelled'));
            return;
          }

          const curTime = renderVideo.currentTime;

          // Draw Canvas Background
          ctx.fillStyle = '#05070f';
          ctx.fillRect(0, 0, targetW, targetH);

          // A. Draw Master Video Frame
          if (renderVideo.videoWidth > 0 && renderVideo.videoHeight > 0) {
            if (activeAspectRatio === '9:16') {
              const targetRatio = 9 / 16;
              const videoRatio = renderVideo.videoWidth / renderVideo.videoHeight;
              if (videoRatio > targetRatio) {
                const cropW = renderVideo.videoHeight * targetRatio;
                const cropX = (renderVideo.videoWidth - cropW) / 2;
                ctx.drawImage(renderVideo, cropX, 0, cropW, renderVideo.videoHeight, 0, 0, targetW, targetH);
              } else {
                const cropH = renderVideo.videoWidth / targetRatio;
                const cropY = (renderVideo.videoHeight - cropH) / 2;
                ctx.drawImage(renderVideo, 0, cropY, renderVideo.videoWidth, cropH, 0, 0, targetW, targetH);
              }
            } else if (activeAspectRatio === '1:1') {
              const cropSize = Math.min(renderVideo.videoWidth, renderVideo.videoHeight);
              const cropX = (renderVideo.videoWidth - cropSize) / 2;
              const cropY = (renderVideo.videoHeight - cropSize) / 2;
              ctx.drawImage(renderVideo, cropX, cropY, cropSize, cropSize, 0, 0, targetW, targetH);
            } else {
              ctx.drawImage(renderVideo, 0, 0, targetW, targetH);
            }
          }

          // B. Draw Active Overlays (Images & Custom Text)
          for (const ovl of overlayClips) {
            if (curTime >= ovl.timelineStartSec && curTime <= (ovl.timelineStartSec + ovl.durationSec)) {
              let xPct = ovl.xPercent ?? 68;
              let yPct = ovl.yPercent ?? 6;
              let wPct = ovl.widthPercent ?? 26;
              let hPct = ovl.heightPercent ?? 18;

              if (ovl.position && ovl.position !== 'custom') {
                if (ovl.position === 'top-left') { xPct = 4; yPct = 4; wPct = 28; hPct = 18; }
                else if (ovl.position === 'top-right') { xPct = 68; yPct = 4; wPct = 28; hPct = 18; }
                else if (ovl.position === 'bottom-left') { xPct = 4; yPct = 76; wPct = 28; hPct = 18; }
                else if (ovl.position === 'bottom-right') { xPct = 68; yPct = 76; wPct = 28; hPct = 18; }
                else if (ovl.position === 'center') { xPct = 25; yPct = 25; wPct = 50; hPct = 50; }
                else if (ovl.position === 'pip') { xPct = 60; yPct = 62; wPct = 35; hPct = 32; }
              }

              const drawX = (xPct * targetW) / 100;
              const drawY = (yPct * targetH) / 100;
              const drawW = (wPct * targetW) / 100;
              const drawH = (hPct * targetH) / 100;

              ctx.save();
              ctx.globalAlpha = ovl.opacity ?? 1.0;

              if (ovl.type === 'text') {
                const scaleRatio = targetW / 1280;
                const fontSize = Math.max(16, Math.round((ovl.fontSize || 24) * scaleRatio));
                ctx.font = `bold ${fontSize}px sans-serif`;
                ctx.fillStyle = ovl.textColor || '#ffffff';
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';
                ctx.shadowColor = 'rgba(0, 0, 0, 0.9)';
                ctx.shadowBlur = 10;
                ctx.shadowOffsetX = 2;
                ctx.shadowOffsetY = 2;
                ctx.fillText(ovl.text || '', drawX + drawW / 2, drawY + drawH / 2);
              } else {
                const img = preloadedImages.get(ovl.id);
                if (img) {
                  ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
                  ctx.shadowBlur = 12;
                  ctx.drawImage(img, drawX, drawY, drawW, drawH);
                }
              }
              ctx.restore();
            }
          }

          // C. Draw Frame Overlays
          if (activeFrame === 'smartphone_916') {
            ctx.save();
            ctx.fillStyle = '#1e293b';
            const notchW = targetW * 0.28;
            const notchH = targetH * 0.025;
            ctx.beginPath();
            ctx.roundRect((targetW - notchW) / 2, targetH * 0.015, notchW, notchH, 12);
            ctx.fill();
            ctx.restore();
          } else if (activeFrame === 'podcast_duo') {
            ctx.save();
            ctx.strokeStyle = '#14b8a6';
            ctx.lineWidth = 4;
            ctx.beginPath();
            ctx.moveTo(targetW / 2, 0);
            ctx.lineTo(targetW / 2, targetH);
            ctx.stroke();
            ctx.restore();
          }

          // Progress updates
          const pct = Math.min(99, Math.round((curTime / totalSec) * 100));
          setExportProgress(pct);
          setExportStatusText(`Rendering frame: ${formatDuration(curTime)} / ${formatDuration(totalSec)} (${pct}%)...`);

          frameCount++;
          if (frameCount % 15 === 0) {
            try {
              setExportPreviewUrl(canvas.toDataURL('image/jpeg', 0.5));
            } catch {}
          }

          if (curTime >= totalSec || renderVideo.ended) {
            renderVideo.pause();
            recorder.stop();
            recorder.onstop = () => {
              audioCtx.close();
              resolve();
            };
            return;
          }

          requestAnimationFrame(renderLoop);
        };

        requestAnimationFrame(renderLoop);
      });

      // 8. Generate Final Blob & Auto-download
      const finalBlob = new Blob(chunks, { type: mimeType });
      const finalBlobUrl = URL.createObjectURL(finalBlob);
      setExportedBlobUrl(finalBlobUrl);
      setExportProgress(100);
      setExportStatusText('✓ Video ready! Downloading file...');

      const fileName = `${currentSession.title.replace(/[^a-zA-Z0-9_-]/g, '_')}_Clipchamp_Edited_${activeAspectRatio.replace(':', 'x')}.mp4`;
      const a = document.createElement('a');
      a.href = finalBlobUrl;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      if (window.electronAPI && typeof window.electronAPI.saveRecordingBlob === 'function') {
        try {
          const buffer = await finalBlob.arrayBuffer();
          await window.electronAPI.saveRecordingBlob(generateId(), fileName, buffer);
        } catch (err) {
          console.warn('Native save fallback:', err);
        }
      }

      confetti({ particleCount: 120, spread: 80 });
      notifyAction('🎉 Success! Timeline changes ke saath video export ho gaya!');
    } catch (err: any) {
      if (err.message !== 'Export cancelled') {
        console.error('Export error:', err);
        setExportStatusText('⚠️ Export failed. Please try again.');
        notifyAction('⚠️ Export failed.');
      }
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportEditedVideo = () => {
    setShowExportModal(true);
    setExportedBlobUrl(null);
    setExportProgress(0);
    setExportStatusText('Ready to composite and export video with all timeline edits.');
  };

  const filteredMedia = useMemo(() => {
    if (mediaFilter === 'all') return mediaLibrary;
    return mediaLibrary.filter(m => m.type === mediaFilter);
  }, [mediaLibrary, mediaFilter]);

  if (!currentSession) {
    return (
      <div className="flex-1 overflow-y-auto p-8 flex flex-col items-center justify-center text-center space-y-4 select-none ambient-gradient">
        <div className="w-16 h-16 rounded-2xl bg-indigo-50 border border-indigo-200 text-indigo-600 flex items-center justify-center shadow-card">
          <Film className="w-8 h-8" />
        </div>
        <h2 className="text-xl font-bold text-brand-text">No Video Loaded for Editing</h2>
        <p className="text-xs text-brand-muted max-w-sm">
          Pehle koi recording record karein ya computer se video file import karein Video Editor Studio me edit karne ke liye.
        </p>
        <div className="flex items-center gap-3 pt-2">
          {onImportFile && (
            <button
              onClick={onImportFile}
              className="px-6 py-3 rounded-2xl bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold shadow-md transition-all active:scale-95 cursor-pointer flex items-center gap-2"
            >
              <FolderOpen className="w-4 h-4" />
              <span>Import Video (.mp4)</span>
            </button>
          )}
          {onNavigateToStudio && (
            <button
              onClick={onNavigateToStudio}
              className="px-5 py-3 rounded-2xl bg-slate-800 hover:bg-slate-750 border border-slate-700 text-slate-200 text-xs font-semibold transition-all cursor-pointer"
            >
              Record New Video
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col h-full overflow-hidden select-none bg-slate-950 text-white font-sans relative">
      <audio ref={bgMusicAudioRef} preload="auto" />

      <input
        ref={fileInputRef}
        type="file"
        multiple
        accept="video/*,image/*,audio/*"
        onChange={handleFilesImported}
        className="hidden"
      />

      {/* FULL WINDOW DRAG-AND-DROP OVERLAY BACKDROP */}
      {isWindowFileDragging && (
        <div className="absolute inset-0 z-50 bg-indigo-950/85 backdrop-blur-md border-4 border-dashed border-indigo-400 flex flex-col items-center justify-center text-center p-8 pointer-events-none animate-in fade-in zoom-in-95">
          <div className="w-20 h-20 rounded-3xl bg-indigo-600/30 border border-indigo-400 text-indigo-300 flex items-center justify-center shadow-2xl mb-4 animate-bounce">
            <Upload className="w-10 h-10" />
          </div>
          <h2 className="text-2xl font-black text-white tracking-wide">
            Drop Media Files Anywhere to Import!
          </h2>
          <p className="text-sm text-indigo-200 mt-2 max-w-md font-medium">
            Release to instantly import your Video (.mp4), Image (.png, .jpg), or Audio (.mp3, .wav) files directly into Clipchamp Studio!
          </p>
        </div>
      )}

      {/* Floating Dragging Tooltip */}
      {dragTooltip && (
        <div
          style={{ left: dragTooltip.x, top: dragTooltip.y }}
          className="fixed z-50 pointer-events-none -translate-x-1/2 px-2.5 py-1 rounded-lg bg-black/90 text-white text-[11px] font-mono font-bold border border-purple-400 shadow-2xl backdrop-blur-md flex items-center gap-1.5"
        >
          <MoveHorizontal className="w-3.5 h-3.5 text-purple-300" />
          <span>{dragTooltip.text}</span>
        </div>
      )}

      {/* ======================================================== */}
      {/* 1. TOP HEADER & CLIPCHAMP ACTION BAR */}
      {/* ======================================================== */}
      <div className="h-14 px-4 bg-slate-900 border-b border-slate-800 flex items-center justify-between shrink-0 z-30">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-tr from-indigo-500 to-purple-600 flex items-center justify-center shadow-md">
            <Scissors className="w-4 h-4 text-white" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-extrabold uppercase tracking-wider text-indigo-400">
                Clipchamp Studio
              </span>
              <span className="text-slate-600">•</span>
              {recordings.length > 1 ? (
                <select
                  value={currentSession.id}
                  onChange={(e) => {
                    const found = recordings.find(r => r.id === e.target.value);
                    if (found && onSelectSession) onSelectSession(found);
                  }}
                  className="bg-slate-800 border border-slate-700 text-slate-200 text-xs font-bold rounded-lg px-2 py-1 cursor-pointer max-w-[200px]"
                >
                  {recordings.map((rec) => (
                    <option key={rec.id} value={rec.id}>
                      {rec.title} ({formatDuration(rec.durationSec)})
                    </option>
                  ))}
                </select>
              ) : (
                <span className="text-xs font-bold text-slate-200 truncate max-w-[180px] sm:max-w-xs">
                  {currentSession.title}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Center: Aspect Ratio */}
        <div className="hidden md:flex items-center gap-2 bg-slate-850 p-1 rounded-xl border border-slate-755">
          <button
            onClick={() => {
              setActiveAspectRatio('16:9');
              if (activeFrame === 'smartphone_916') setActiveFrame('none');
            }}
            className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-all ${
              activeAspectRatio === '16:9' ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-400 hover:text-white'
            }`}
            title="16:9 Landscape"
          >
            16:9 Landscape
          </button>
          <button
            onClick={() => {
              setActiveAspectRatio('9:16');
              setActiveFrame('smartphone_916');
            }}
            className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-all ${
              activeAspectRatio === '9:16' ? 'bg-indigo-600 text-white shadow-sm ring-1 ring-indigo-400/50' : 'text-slate-400 hover:text-white'
            }`}
            title="9:16 Shorts"
          >
            9:16 Shorts
          </button>
          <button
            onClick={() => {
              setActiveAspectRatio('1:1');
              if (activeFrame === 'smartphone_916') setActiveFrame('none');
            }}
            className={`px-2.5 py-1 rounded-lg text-xs font-bold transition-all ${
              activeAspectRatio === '1:1' ? 'bg-indigo-600 text-white shadow-sm' : 'text-slate-400 hover:text-white'
            }`}
            title="1:1 Square"
          >
            1:1 Square
          </button>
        </div>

        {/* Right: Import Video, Add Media, Undo & Export Button */}
        <div className="flex items-center gap-2">
          {onNavigateToSeparateVideos && (
            <button
              onClick={onNavigateToSeparateVideos}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-750 border border-slate-700 text-xs font-semibold text-slate-300 hover:text-white transition-colors cursor-pointer"
              title="Open Separate Videos (Host & Guest participant separation)"
            >
              <Users className="w-3.5 h-3.5 text-cyan-400" />
              <span className="hidden sm:inline">Separate Videos</span>
            </button>
          )}

          {onImportFile && (
            <button
              onClick={onImportFile}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold shadow-md transition-all cursor-pointer active:scale-95"
              title="Import Video File (.mp4) from Computer to Edit"
            >
              <FolderOpen className="w-3.5 h-3.5" />
              <span>Import Video</span>
            </button>
          )}

          <button
            onClick={() => fileInputRef.current?.click()}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-750 border border-slate-700 text-xs font-semibold text-indigo-300 hover:text-indigo-200 transition-colors cursor-pointer"
            title="Import Media Assets (Image / Audio)"
          >
            <Upload className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Add Media</span>
          </button>

          <button
            onClick={handleUndo}
            disabled={history.length === 0}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-750 border border-slate-700 text-xs font-semibold text-slate-300 disabled:opacity-40 transition-colors cursor-pointer"
            title="Undo (Ctrl+Z)"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Undo</span>
          </button>

          <button
            onClick={handleExportEditedVideo}
            disabled={isExporting}
            className="flex items-center gap-2 px-4 py-1.5 rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white text-xs font-bold shadow-md transition-all cursor-pointer disabled:opacity-50"
          >
            {isExporting ? (
              <>
                <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Exporting ({exportProgress}%)...</span>
              </>
            ) : (
              <>
                <Download className="w-3.5 h-3.5" />
                <span>Export Video</span>
              </>
            )}
          </button>
        </div>
      </div>

      {actionMessage && (
        <div className="absolute top-16 left-1/2 -translate-x-1/2 z-50 px-4 py-2 bg-indigo-900/90 text-white border border-indigo-500/50 rounded-2xl shadow-2xl backdrop-blur-md text-xs font-bold flex items-center gap-2 animate-in fade-in slide-in-from-top-2">
          <Sparkles className="w-4 h-4 text-indigo-300 shrink-0" />
          <span>{actionMessage}</span>
        </div>
      )}

      {/* ======================================================== */}
      {/* 2. MAIN WORKSPACE: LEFT TOOLS + CANVAS PREVIEW */}
      {/* ======================================================== */}
      {/* 2. MAIN WORKSPACE: LEFT TOOLS + CANVAS PREVIEW */}
      <div className="flex-1 flex overflow-hidden min-h-0 relative">
        {/* CLIPCHAMP SLIM NAVIGATION RAIL (52px DOCK) */}
        <div className="w-13 bg-slate-950 border-r border-slate-800 flex flex-col items-center py-2.5 gap-2.5 shrink-0 z-20 select-none">
          <button
            onClick={() => {
              setActiveLeftTab('media');
              if (isSidebarCollapsed) setIsSidebarCollapsed(false);
            }}
            className={`w-10 h-10 rounded-xl flex flex-col items-center justify-center gap-0.5 transition-all cursor-pointer ${
              activeLeftTab === 'media' && !isSidebarCollapsed
                ? 'bg-indigo-600 text-white shadow-glow'
                : 'text-slate-400 hover:text-white hover:bg-slate-850'
            }`}
            title="Your Media"
          >
            <FolderOpen className="w-4 h-4" />
            <span className="text-[8px] font-bold">Media</span>
          </button>

          <button
            onClick={() => {
              setActiveLeftTab('frames');
              if (isSidebarCollapsed) setIsSidebarCollapsed(false);
            }}
            className={`w-10 h-10 rounded-xl flex flex-col items-center justify-center gap-0.5 transition-all cursor-pointer ${
              activeLeftTab === 'frames' && !isSidebarCollapsed
                ? 'bg-indigo-600 text-white shadow-glow'
                : 'text-slate-400 hover:text-white hover:bg-slate-850'
            }`}
            title="Frames & Overlays"
          >
            <Layers className="w-4 h-4" />
            <span className="text-[8px] font-bold">Frames</span>
          </button>

          <button
            onClick={() => {
              setActiveLeftTab('audio');
              if (isSidebarCollapsed) setIsSidebarCollapsed(false);
            }}
            className={`w-10 h-10 rounded-xl flex flex-col items-center justify-center gap-0.5 transition-all cursor-pointer ${
              activeLeftTab === 'audio' && !isSidebarCollapsed
                ? 'bg-indigo-600 text-white shadow-glow'
                : 'text-slate-400 hover:text-white hover:bg-slate-850'
            }`}
            title="Audio & Music"
          >
            <Music className="w-4 h-4" />
            <span className="text-[8px] font-bold">Audio</span>
          </button>

          <button
            onClick={handleAddTextOverlay}
            className="w-10 h-10 rounded-xl flex flex-col items-center justify-center gap-0.5 transition-all cursor-pointer text-slate-400 hover:text-purple-300 hover:bg-purple-950/40"
            title="Add Text Title"
          >
            <Type className="w-4 h-4 text-purple-400" />
            <span className="text-[8px] font-bold">Text</span>
          </button>

          {/* Spacer */}
          <div className="flex-1" />

          {/* Toggle Collapse Arrow Button */}
          <button
            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
            className="w-8 h-8 rounded-lg bg-slate-900 hover:bg-slate-800 text-slate-400 hover:text-white flex items-center justify-center transition-colors cursor-pointer"
            title={isSidebarCollapsed ? 'Expand panel' : 'Collapse panel'}
          >
            {isSidebarCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>
        </div>

        {/* EXPANDABLE / RESIZABLE ASSET PANEL */}
        <div
          style={{ width: isSidebarCollapsed ? 0 : `${sidebarWidth}px` }}
          className={`bg-slate-900/95 border-r border-slate-800 flex flex-col shrink-0 overflow-y-auto transition-[width] ${
            isResizingSidebar ? 'transition-none select-none' : 'duration-150'
          } ${isSidebarCollapsed ? 'overflow-hidden' : ''}`}
        >
          <div className="flex items-center border-b border-slate-800 p-1 bg-slate-950/40">
            <button
              onClick={() => setActiveLeftTab('media')}
              className={`flex-1 py-2 text-[11px] font-bold rounded-lg transition-all flex flex-col items-center gap-1 ${
                activeLeftTab === 'media' ? 'bg-indigo-600/30 text-indigo-300 border border-indigo-500/40' : 'text-slate-400 hover:text-white'
              }`}
            >
              <Film className="w-4 h-4" />
              <span>Media ({mediaLibrary.length})</span>
            </button>

            <button
              onClick={() => setActiveLeftTab('frames')}
              className={`flex-1 py-2 text-[11px] font-bold rounded-lg transition-all flex flex-col items-center gap-1 ${
                activeLeftTab === 'frames' ? 'bg-indigo-600/30 text-indigo-300 border border-indigo-500/40' : 'text-slate-400 hover:text-white'
              }`}
            >
              <Layers className="w-4 h-4" />
              <span>Frames</span>
            </button>

            <button
              onClick={() => setActiveLeftTab('audio')}
              className={`flex-1 py-2 text-[11px] font-bold rounded-lg transition-all flex flex-col items-center gap-1 ${
                activeLeftTab === 'audio' ? 'bg-indigo-600/30 text-indigo-300 border border-indigo-500/40' : 'text-slate-400 hover:text-white'
              }`}
            >
              <Music className="w-4 h-4" />
              <span>Audio</span>
            </button>
          </div>

          {/* TAB 1: MEDIA LIBRARY */}
          {activeLeftTab === 'media' && (
            <div className="p-4 space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-300 flex items-center gap-1.5">
                    <FolderOpen className="w-3.5 h-3.5 text-indigo-400" />
                    Media & Assets
                  </h3>
                  <p className="text-[11px] text-slate-400 mt-0.5">
                    Drag files onto timeline or use library presets.
                  </p>
                </div>
              </div>

              {/* Import Video & Add Text Buttons */}
              <div className="flex flex-col gap-2">
                {onImportFile && (
                  <button
                    type="button"
                    onClick={onImportFile}
                    className="w-full py-2.5 px-3 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold flex items-center justify-center gap-2 shadow-sm transition-all cursor-pointer"
                    title="Import a new video file (.mp4) to edit"
                  >
                    <FolderOpen className="w-4 h-4" />
                    <span>Import Video to Studio</span>
                  </button>
                )}

                <button
                  type="button"
                  onClick={handleAddTextOverlay}
                  className="w-full py-2 px-3 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white text-xs font-bold flex items-center justify-center gap-2 shadow-sm transition-all cursor-pointer"
                >
                  <Type className="w-4 h-4" />
                  <span>+ Add Text / Title Overlay</span>
                </button>
              </div>

              {/* DEDICATED DRAG-AND-DROP ZONE IN MEDIA PANEL */}
              <div
                onDragOver={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  e.dataTransfer.dropEffect = 'copy';
                  setIsMediaDropZoneHovered(true);
                }}
                onDragEnter={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsMediaDropZoneHovered(true);
                }}
                onDragLeave={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsMediaDropZoneHovered(false);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setIsMediaDropZoneHovered(false);
                  if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
                    processDroppedFiles(e.dataTransfer.files, 'library');
                  }
                }}
                onClick={() => fileInputRef.current?.click()}
                className={`w-full py-3.5 px-4 rounded-xl border-2 border-dashed transition-all cursor-pointer flex flex-col items-center justify-center text-center gap-1.5 group ${
                  isMediaDropZoneHovered
                    ? 'border-indigo-400 bg-indigo-600/30 ring-2 ring-indigo-400/50 scale-[1.02]'
                    : 'border-indigo-500/50 hover:border-indigo-400 bg-indigo-600/10 hover:bg-indigo-600/20'
                }`}
              >
                <div className="w-8 h-8 rounded-full bg-indigo-500/20 group-hover:bg-indigo-500/30 flex items-center justify-center text-indigo-300">
                  <Upload className="w-4 h-4 group-hover:-translate-y-0.5 transition-transform" />
                </div>
                <div>
                  <div className="text-xs font-bold text-white">
                    {isMediaDropZoneHovered ? 'Release to Import Files!' : 'Drag & Drop Files Here'}
                  </div>
                  <div className="text-[10px] text-indigo-300 mt-0.5">
                    or click to browse from computer
                  </div>
                </div>
              </div>

              {/* Filter Tabs */}
              <div className="flex rounded-lg bg-slate-800/80 p-0.5 text-[10px] font-bold">
                <button
                  type="button"
                  onClick={() => setMediaFilter('all')}
                  className={`flex-1 py-1 rounded-md transition-all ${
                    mediaFilter === 'all' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  All ({mediaLibrary.length})
                </button>
                <button
                  type="button"
                  onClick={() => setMediaFilter('video')}
                  className={`flex-1 py-1 rounded-md transition-all ${
                    mediaFilter === 'video' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  Videos
                </button>
                <button
                  type="button"
                  onClick={() => setMediaFilter('image')}
                  className={`flex-1 py-1 rounded-md transition-all ${
                    mediaFilter === 'image' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  Images
                </button>
                <button
                  type="button"
                  onClick={() => setMediaFilter('audio')}
                  className={`flex-1 py-1 rounded-md transition-all ${
                    mediaFilter === 'audio' ? 'bg-indigo-600 text-white' : 'text-slate-400 hover:text-white'
                  }`}
                >
                  Audio
                </button>
              </div>

              {/* Asset List */}
              <div className="space-y-2.5 max-h-[460px] overflow-y-auto pr-1">
                {filteredMedia.map((asset) => (
                  <div
                    key={asset.id}
                    draggable={true}
                    onDragStart={(e) => {
                      e.dataTransfer.setData(
                        'application/json',
                        JSON.stringify({ type: 'internal-asset', assetId: asset.id })
                      );
                      e.dataTransfer.effectAllowed = 'copy';
                    }}
                    className="p-2.5 rounded-xl bg-slate-850/80 border border-slate-750 hover:border-indigo-500/60 transition-all flex flex-col gap-2 group cursor-grab active:cursor-grabbing hover:shadow-md"
                    title="Drag directly onto timeline or click + Add to Timeline"
                  >
                    <div className="flex items-center gap-2.5">
                      <GripVertical className="w-3.5 h-3.5 text-slate-500 group-hover:text-slate-300 shrink-0" />
                      
                      <div className="w-14 h-11 rounded-lg bg-slate-900 border border-slate-700/80 flex items-center justify-center overflow-hidden shrink-0 relative">
                        {asset.type === 'image' ? (
                          <img src={asset.url} alt={asset.name} className="w-full h-full object-contain p-1" />
                        ) : asset.type === 'video' ? (
                          asset.thumbnailUrl ? (
                            <img src={asset.thumbnailUrl} alt={asset.name} className="w-full h-full object-cover" />
                          ) : (
                            <Film className="w-5 h-5 text-indigo-400" />
                          )
                        ) : (
                          <div className="w-full h-full bg-emerald-950/60 flex items-center justify-center">
                            <Music className="w-5 h-5 text-emerald-400" />
                          </div>
                        )}
                        <div className="absolute bottom-0.5 right-0.5 px-1 rounded bg-black/80 font-mono text-[8px] text-slate-200">
                          {formatDuration(asset.durationSec)}
                        </div>
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span
                            className={`text-[9px] px-1.5 py-0.2 rounded font-extrabold uppercase font-mono ${
                              asset.type === 'video'
                                ? 'bg-indigo-500/20 text-indigo-300 border border-indigo-500/30'
                                : asset.type === 'image'
                                ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
                                : 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                            }`}
                          >
                            {asset.type}
                          </span>
                          {asset.isStock && (
                            <span className="text-[8px] px-1 rounded bg-amber-500/20 text-amber-300 font-bold">
                              Stock
                            </span>
                          )}
                        </div>
                        <div className="text-xs font-bold text-slate-200 truncate mt-0.5" title={asset.name}>
                          {asset.name}
                        </div>
                        <div className="text-[10px] text-slate-400">
                          {asset.fileSize ? formatFileSize(asset.fileSize) : `${asset.durationSec.toFixed(1)}s duration`}
                        </div>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => handleAddAssetToTimeline(asset)}
                      className="w-full py-1.5 px-3 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-[11px] font-bold shadow-sm transition-all flex items-center justify-center gap-1.5 cursor-pointer active:scale-95"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      <span>+ Add to Timeline</span>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* TAB 2: FRAMES */}
          {activeLeftTab === 'frames' && (
            <div className="p-4 space-y-4">
              <div>
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-300 flex items-center gap-1.5">
                  <Layers className="w-3.5 h-3.5 text-indigo-400" />
                  Decorative Frames & Borders
                </h3>
              </div>

              <div className="space-y-2.5">
                <button
                  type="button"
                  onClick={() => setActiveFrame('none')}
                  className={`w-full p-2.5 rounded-xl border text-left flex items-center justify-between transition-all cursor-pointer ${
                    activeFrame === 'none'
                      ? 'bg-indigo-600/20 border-indigo-500 text-white ring-1 ring-indigo-500'
                      : 'bg-slate-850/60 border-slate-750 text-slate-300 hover:bg-slate-800'
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    <Monitor className="w-4 h-4 text-slate-400" />
                    <div>
                      <div className="text-xs font-bold">Standard Full Frame</div>
                      <div className="text-[10px] text-slate-400">Clean video (No border)</div>
                    </div>
                  </div>
                  {activeFrame === 'none' && <Check className="w-4 h-4 text-indigo-400" />}
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setActiveFrame('smartphone_916');
                    setActiveAspectRatio('9:16');
                  }}
                  className={`w-full p-2.5 rounded-xl border text-left flex items-center justify-between transition-all cursor-pointer ${
                    activeFrame === 'smartphone_916'
                      ? 'bg-indigo-600/20 border-indigo-500 text-white ring-1 ring-indigo-500'
                      : 'bg-slate-850/60 border-slate-750 text-slate-300 hover:bg-slate-800'
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    <Smartphone className="w-4 h-4 text-indigo-400" />
                    <div>
                      <div className="text-xs font-bold">Smartphone 9:16 Notch Frame</div>
                      <div className="text-[10px] text-indigo-400/80">YouTube Shorts & Reels</div>
                    </div>
                  </div>
                  {activeFrame === 'smartphone_916' && <Check className="w-4 h-4 text-indigo-400" />}
                </button>

                <button
                  type="button"
                  onClick={() => setActiveFrame('podcast_duo')}
                  className={`w-full p-2.5 rounded-xl border text-left flex items-center justify-between transition-all cursor-pointer ${
                    activeFrame === 'podcast_duo'
                      ? 'bg-indigo-600/20 border-indigo-500 text-white ring-1 ring-indigo-500'
                      : 'bg-slate-850/60 border-slate-750 text-slate-300 hover:bg-slate-800'
                  }`}
                >
                  <div className="flex items-center gap-2.5">
                    <Split className="w-4 h-4 text-teal-400" />
                    <div>
                      <div className="text-xs font-bold">Podcast Duo Split Frame</div>
                      <div className="text-[10px] text-teal-400/80">Host & Guest neon boundary</div>
                    </div>
                  </div>
                  {activeFrame === 'podcast_duo' && <Check className="w-4 h-4 text-teal-400" />}
                </button>
              </div>
            </div>
          )}

          {/* TAB 3: AUDIO */}
          {activeLeftTab === 'audio' && (
            <div className="p-4 space-y-4">
              <div>
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-300 flex items-center gap-1.5">
                  <Music className="w-3.5 h-3.5 text-indigo-400" />
                  Audio Tracks & Controls
                </h3>
              </div>

              <button
                type="button"
                onClick={handleToggleDetachAudio}
                className={`w-full py-2.5 px-4 rounded-xl border text-xs font-bold flex items-center justify-center gap-2 transition-all cursor-pointer ${
                  isAudioDetached
                    ? 'bg-rose-500/20 border-rose-500/50 text-rose-300 hover:bg-rose-500/30'
                    : 'bg-indigo-600 hover:bg-indigo-500 border-indigo-500 text-white shadow-md'
                }`}
              >
                <Music className="w-4 h-4" />
                <span>{isAudioDetached ? 'Re-attach Audio to Video' : 'Detach Audio from Video'}</span>
              </button>

              <button
                type="button"
                disabled={isDownloadingAudio}
                onClick={handleDownloadAudioOnly}
                className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-bold text-xs shadow-md transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
              >
                <Download className="w-4 h-4" />
                <span>Download Audio Only (.MP3 / .WAV)</span>
              </button>

              <div className="p-3 bg-slate-850/90 rounded-xl border border-slate-750 space-y-2.5">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-bold text-slate-200 flex items-center gap-1.5">
                    {isVideoAudioMuted ? <VolumeX className="w-4 h-4 text-rose-400" /> : <Volume2 className="w-4 h-4 text-emerald-400" />}
                    Video Audio Track
                  </span>
                  <span className={`text-[10px] px-2 py-0.5 rounded font-mono font-bold ${
                    isVideoAudioMuted ? 'bg-rose-500/20 text-rose-300' : 'bg-emerald-500/20 text-emerald-300'
                  }`}>
                    {isVideoAudioMuted ? 'MUTED' : 'ACTIVE'}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={handleToggleMuteVideoTrack}
                  className={`w-full py-2 px-3 rounded-lg border text-xs font-bold transition-all flex items-center justify-center gap-2 cursor-pointer ${
                    isVideoAudioMuted
                      ? 'bg-rose-600 hover:bg-rose-500 text-white border-rose-500 shadow-sm'
                      : 'bg-slate-850 hover:bg-slate-800 text-slate-300 border-slate-700'
                  }`}
                >
                  {isVideoAudioMuted ? <Volume2 className="w-3.5 h-3.5" /> : <VolumeX className="w-3.5 h-3.5 text-rose-400" />}
                  <span>{isVideoAudioMuted ? 'Unmute Video Audio' : 'Mute Video Audio'}</span>
                </button>
              </div>
            </div>
          )}
        </div>

        {/* RESIZABLE SPLITTER: SIDEBAR <-> CANVAS */}
        {!isSidebarCollapsed && (
          <div
            onMouseDown={handleStartSidebarResize}
            className={`w-1.5 hover:w-2 bg-slate-850 hover:bg-indigo-500 cursor-col-resize transition-all z-20 shrink-0 relative group flex items-center justify-center select-none ${
              isResizingSidebar ? 'w-2 bg-indigo-500' : ''
            }`}
            title="Drag left/right to resize panel width"
          >
            <div className="w-0.5 h-6 bg-slate-600 group-hover:bg-white rounded-full transition-colors" />
          </div>
        )}

        {/* ======================================================== */}
        {/* CENTER STAGE: VIDEO CANVAS & MANUAL RESIZE TRANSFORM BOX */}
        {/* ======================================================== */}
        <div
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
            e.dataTransfer.dropEffect = 'copy';
          }}
          onDrop={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
              processDroppedFiles(e.dataTransfer.files, 'canvas', playheadSec);
            }
          }}
          className="flex-1 flex flex-col items-center justify-center p-6 bg-slate-950 relative overflow-hidden min-h-0"
        >
          <div
            ref={playerStageRef}
            className={`relative bg-black shadow-2xl flex items-center justify-center transition-all duration-300 max-h-[460px] ${
              activeAspectRatio === '9:16'
                ? 'w-[250px] aspect-[9/16] rounded-3xl border-4 border-indigo-500/80 ring-4 ring-indigo-500/20'
                : activeAspectRatio === '1:1'
                ? 'w-[360px] aspect-square rounded-2xl border-2 border-indigo-500/60'
                : activeAspectRatio === '21:9'
                ? 'w-full max-w-4xl aspect-[21/9] rounded-2xl border border-slate-800'
                : 'w-full max-w-3xl aspect-video rounded-2xl border border-slate-800'
            }`}
          >
            {/* FRAME OVERLAY 1: Smartphone 9:16 Notch */}
            {activeFrame === 'smartphone_916' && (
              <>
                <div className="absolute top-2.5 w-16 h-3 bg-slate-800 rounded-full z-20 pointer-events-none shadow-md" />
                <div className="absolute top-3 right-4 flex items-center gap-1 z-20 pointer-events-none text-[9px] font-mono text-white/80">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                  <span>9:16</span>
                </div>
              </>
            )}

            {/* FRAME OVERLAY 2: Podcast Duo Split Frame */}
            {activeFrame === 'podcast_duo' && (
              <>
                <div className="absolute inset-0 border-2 border-teal-500/70 pointer-events-none z-20 shadow-[0_0_15px_rgba(20,184,166,0.3)] rounded-xl" />
                <div className="absolute top-0 bottom-0 left-1/2 w-0.5 bg-teal-400 pointer-events-none z-20 shadow-[0_0_8px_rgba(20,184,166,1)]" />
              </>
            )}

            {/* ======================================================== */}
            {/* ACTIVE OVERLAY: INTERACTIVE BOUNDING BOX WITH 4 CORNER RESIZE HANDLES */}
            {/* Allows user to MANUALLY RESIZE on canvas with mouse handles! */}
            {/* ======================================================== */}
            {(() => {
              // Get all active overlay clips at the playhead, plus the selected clip if user is editing it
              const visibleClips = overlayClips.filter(
                c => (playheadSec >= c.timelineStartSec && playheadSec <= c.timelineStartSec + c.durationSec) ||
                     (selectedOverlayClipId === c.id)
              );

              return visibleClips.map((ovl) => {
                let clipX = ovl.xPercent ?? 68;
                let clipY = ovl.yPercent ?? 6;
                let clipW = ovl.widthPercent ?? 26;
                let clipH = ovl.heightPercent ?? 18;

                if (ovl.position && ovl.position !== 'custom') {
                  if (ovl.position === 'top-left') { clipX = 4; clipY = 4; clipW = 28; clipH = 18; }
                  else if (ovl.position === 'top-right') { clipX = 68; clipY = 4; clipW = 28; clipH = 18; }
                  else if (ovl.position === 'bottom-left') { clipX = 4; clipY = 76; clipW = 28; clipH = 18; }
                  else if (ovl.position === 'bottom-right') { clipX = 68; clipY = 76; clipW = 28; clipH = 18; }
                  else if (ovl.position === 'center') { clipX = 25; clipY = 25; clipW = 50; clipH = 50; }
                  else if (ovl.position === 'pip') { clipX = 60; clipY = 62; clipW = 35; clipH = 32; }
                }

                const isSelected = selectedOverlayClipId === ovl.id;

                return (
                  <div
                    key={ovl.id}
                    style={{
                      left: `${clipX}%`,
                      top: `${clipY}%`,
                      width: `${clipW}%`,
                      height: `${clipH}%`
                    }}
                    onMouseDown={(e) => handleStartCanvasTransform(e, 'move', ovl)}
                    className={`absolute z-30 select-none group/transform cursor-move ${
                      isSelected
                        ? 'border-2 border-dashed border-purple-400 ring-2 ring-purple-500/30 shadow-lg'
                        : 'border border-transparent hover:border-purple-400/60'
                    }`}
                  >
                    {/* Overlay Content (Image or Custom Text) */}
                    {ovl.type === 'text' ? (
                      <div
                        contentEditable={isSelected}
                        suppressContentEditableWarning={true}
                        onBlur={(e) => {
                          const newTxt = e.currentTarget.innerText;
                          pushToHistory();
                          setOverlayClips(prev =>
                            prev.map(c => (c.id === ovl.id ? { ...c, text: newTxt } : c))
                          );
                        }}
                        style={{
                          color: ovl.textColor || '#ffffff',
                          fontSize: `${ovl.fontSize || 24}px`,
                          opacity: ovl.opacity ?? 1.0
                        }}
                        className="w-full h-full flex items-center justify-center font-bold text-center drop-shadow-md select-text"
                      >
                        {ovl.text || 'Double click to edit text'}
                      </div>
                    ) : (
                      <img
                        src={ovl.url}
                        alt={ovl.name}
                        className="w-full h-full object-contain drop-shadow-2xl pointer-events-none"
                        style={{ opacity: ovl.opacity ?? 1.0 }}
                      />
                    )}

                    {/* 8 INTERACTIVE RESIZE HANDLES (4 Corners + 4 Edges) */}
                    {isSelected && (
                      <>
                        {/* 4 Corner Handles */}
                        <div
                          onMouseDown={(e) => handleStartCanvasTransform(e, 'nw', ovl)}
                          className="absolute -top-2 -left-2 w-4 h-4 rounded-full bg-purple-500 border-2 border-white shadow-lg cursor-nwse-resize z-40 hover:scale-125 transition-transform"
                          title="Drag to resize top-left"
                        />
                        <div
                          onMouseDown={(e) => handleStartCanvasTransform(e, 'ne', ovl)}
                          className="absolute -top-2 -right-2 w-4 h-4 rounded-full bg-purple-500 border-2 border-white shadow-lg cursor-nesw-resize z-40 hover:scale-125 transition-transform"
                          title="Drag to resize top-right"
                        />
                        <div
                          onMouseDown={(e) => handleStartCanvasTransform(e, 'sw', ovl)}
                          className="absolute -bottom-2 -left-2 w-4 h-4 rounded-full bg-purple-500 border-2 border-white shadow-lg cursor-nesw-resize z-40 hover:scale-125 transition-transform"
                          title="Drag to resize bottom-left"
                        />
                        <div
                          onMouseDown={(e) => handleStartCanvasTransform(e, 'se', ovl)}
                          className="absolute -bottom-2 -right-2 w-4 h-4 rounded-full bg-purple-500 border-2 border-white shadow-lg cursor-nwse-resize z-40 hover:scale-125 transition-transform"
                          title="Drag to resize bottom-right"
                        />

                        {/* 4 Edge Handles */}
                        <div
                          onMouseDown={(e) => handleStartCanvasTransform(e, 'n', ovl)}
                          className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-5 h-3 bg-purple-600 rounded-sm border border-white cursor-ns-resize z-40 shadow hover:bg-purple-500"
                          title="Drag top edge to stretch height"
                        />
                        <div
                          onMouseDown={(e) => handleStartCanvasTransform(e, 's', ovl)}
                          className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-5 h-3 bg-purple-600 rounded-sm border border-white cursor-ns-resize z-40 shadow hover:bg-purple-500"
                          title="Drag bottom edge to stretch height"
                        />
                        <div
                          onMouseDown={(e) => handleStartCanvasTransform(e, 'w', ovl)}
                          className="absolute top-1/2 -left-1.5 -translate-y-1/2 w-3 h-5 bg-purple-600 rounded-sm border border-white cursor-ew-resize z-40 shadow hover:bg-purple-500"
                          title="Drag left edge to stretch width"
                        />
                        <div
                          onMouseDown={(e) => handleStartCanvasTransform(e, 'e', ovl)}
                          className="absolute top-1/2 -right-1.5 -translate-y-1/2 w-3 h-5 bg-purple-600 rounded-sm border border-white cursor-ew-resize z-40 shadow hover:bg-purple-500"
                          title="Drag right edge to stretch width"
                        />

                        {/* Mini floating size indicator badge */}
                        <div className="absolute -bottom-7 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded bg-black/90 text-purple-300 border border-purple-400/50 text-[9px] font-mono font-bold whitespace-nowrap shadow-lg pointer-events-none">
                          {ovl.type === 'text' ? '🔤 ' : '🖼️ '}Size: {Math.round(clipW)}% × {Math.round(clipH)}%
                        </div>
                      </>
                    )}
                  </div>
                );
              });
            })()}

            {/* HTML5 Video Canvas Stream */}
            {currentSession.blobUrl ? (
              <video
                ref={videoRef}
                src={currentSession.blobUrl}
                playsInline
                muted={isMuted || isVideoAudioMuted}
                onClick={() => setIsPlaying(!isPlaying)}
                className={`w-full h-full pointer-events-auto cursor-pointer ${
                  activeAspectRatio === '9:16' ? 'object-cover' : 'object-contain'
                }`}
              />
            ) : currentSession.thumbnailUrl ? (
              <img
                src={currentSession.thumbnailUrl}
                alt="Feed"
                className={`w-full h-full ${activeAspectRatio === '9:16' ? 'object-cover' : 'object-contain'}`}
              />
            ) : (
              <div className="text-slate-500 text-xs">Video Feed Ready</div>
            )}

            {/* Canvas Transport Play/Pause */}
            <button
              onClick={() => setIsPlaying(!isPlaying)}
              className="absolute inset-0 m-auto w-12 h-12 rounded-full bg-black/60 hover:bg-black/80 backdrop-blur-md text-white flex items-center justify-center shadow-lg transition-transform hover:scale-105 pointer-events-auto cursor-pointer z-20"
              title={isPlaying ? 'Pause (Space)' : 'Play (Space)'}
            >
              {isPlaying ? <Pause className="w-5 h-5 fill-white" /> : <Play className="w-5 h-5 fill-white ml-0.5" />}
            </button>

            {/* Floating Timecode Indicator */}
            <div className="absolute bottom-3 left-3 px-2.5 py-1 rounded-lg bg-black/80 backdrop-blur-md text-[10px] text-white font-mono pointer-events-none z-20">
              {formatDuration(playheadSec)} / {formatDuration(totalTimelineDuration)}
            </div>
          </div>
        </div>
      </div>

      {/* ======================================================== */}
      {/* RESIZABLE SPLITTER: PREVIEW STAGE <-> TIMELINE */}
      {/* ======================================================== */}
      <div
        onMouseDown={handleStartTimelineResize}
        className={`h-2 hover:h-2.5 bg-slate-850 hover:bg-indigo-500/80 cursor-row-resize transition-all z-30 shrink-0 relative group flex items-center justify-center select-none border-t border-b border-slate-800 ${
          isResizingTimeline ? 'h-2.5 bg-indigo-500' : ''
        }`}
        title="Drag up or down to resize timeline height (Clipchamp style)"
      >
        <div className="w-14 h-1 bg-slate-600 group-hover:bg-white rounded-full transition-colors flex items-center justify-center gap-0.5">
          <div className="w-1 h-1 rounded-full bg-slate-400 group-hover:bg-white" />
          <div className="w-1 h-1 rounded-full bg-slate-400 group-hover:bg-white" />
          <div className="w-1 h-1 rounded-full bg-slate-400 group-hover:bg-white" />
        </div>
        <span className="absolute right-4 text-[9px] font-mono text-slate-500 group-hover:text-indigo-200 pointer-events-none">
          ↕ Drag to resize timeline ({timelineHeight}px)
        </span>
      </div>

      {/* ======================================================== */}
      {/* 3. CLIPCHAMP MULTI-TRACK TIMELINE EDITOR */}
      {/* ======================================================== */}
      <div
        style={{ height: `${timelineHeight}px` }}
        className="bg-slate-900 border-t border-slate-800 flex flex-col shrink-0 select-none z-20"
      >
        {/* TIMELINE TOOLBAR 1: PRIMARY ACTIONS */}
        <div className="h-10 px-4 bg-slate-850 border-b border-slate-800 flex items-center justify-between gap-3 shrink-0 relative">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleSplitAtPlayhead}
              className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold shadow transition-all active:scale-95 cursor-pointer"
              title="Split selected or active clip (Shortcut: S)"
            >
              <Scissors className="w-3.5 h-3.5" />
              <span>Split</span>
              <kbd className="px-1 py-0.2 rounded bg-indigo-700 text-[9px] font-mono">S</kbd>
            </button>

            <button
              type="button"
              onClick={handleDeleteSelectedClip}
              disabled={
                timelineClips.length <= 1 &&
                !selectedAudioClipId &&
                !selectedOverlayClipId &&
                !selectedMusicClipId
              }
              className="flex items-center gap-1 px-2.5 py-1 rounded-lg bg-slate-800 hover:bg-rose-600/30 hover:text-rose-300 border border-slate-700 text-slate-300 text-xs font-semibold disabled:opacity-40 transition-all cursor-pointer"
              title="Delete selected clip"
            >
              <Trash2 className="w-3.5 h-3.5 text-rose-400" />
              <span>Delete</span>
            </button>

            {/* "+ ADD TRACK" DROPDOWN BUTTON */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowAddTrackMenu(!showAddTrackMenu)}
                className="flex items-center gap-1.5 px-3 py-1 rounded-lg bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-500 hover:to-indigo-500 text-white text-xs font-bold shadow transition-all cursor-pointer"
              >
                <Plus className="w-3.5 h-3.5" />
                <span>+ Add Track</span>
                <ChevronDown className="w-3 h-3 ml-0.5" />
              </button>

              {showAddTrackMenu && (
                <div className="absolute top-8 left-0 w-60 bg-slate-850 border border-slate-700 rounded-xl shadow-2xl p-1.5 z-50 animate-in fade-in zoom-in-95">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 px-2 py-1">
                    Timeline Tracks
                  </div>

                  <button
                    type="button"
                    onClick={() => handleAddNewTrack('overlay')}
                    className="w-full text-left px-2.5 py-2 rounded-lg hover:bg-purple-600/20 hover:text-purple-300 text-slate-200 text-xs font-semibold flex items-center gap-2 cursor-pointer transition-colors"
                  >
                    <Layers className="w-4 h-4 text-purple-400" />
                    <div>
                      <div>+ Overlay / Image Track</div>
                      <div className="text-[10px] text-slate-400 font-normal">Photos, watermarks & text</div>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => handleAddNewTrack('audio-music')}
                    className="w-full text-left px-2.5 py-2 rounded-lg hover:bg-teal-600/20 hover:text-teal-300 text-slate-200 text-xs font-semibold flex items-center gap-2 cursor-pointer transition-colors"
                  >
                    <Music className="w-4 h-4 text-teal-400" />
                    <div>
                      <div>+ Audio / Music Track</div>
                      <div className="text-[10px] text-slate-400 font-normal">Background music & SFX</div>
                    </div>
                  </button>
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={handleToggleDetachAudio}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-semibold transition-all cursor-pointer ${
                isAudioDetached
                  ? 'bg-emerald-600/20 border-emerald-500/50 text-emerald-300'
                  : 'bg-slate-800 hover:bg-slate-750 border-slate-700 text-slate-300 hover:text-white'
              }`}
            >
              <Music className="w-3.5 h-3.5 text-emerald-400" />
              <span>{isAudioDetached ? 'Detached ✓' : 'Detach Audio'}</span>
            </button>

            <button
              type="button"
              onClick={handleToggleMuteVideoTrack}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-semibold transition-all cursor-pointer ${
                isVideoAudioMuted
                  ? 'bg-rose-600/25 border-rose-500 text-rose-300'
                  : 'bg-slate-800 hover:bg-slate-750 border-slate-700 text-slate-300 hover:text-white'
              }`}
            >
              {isVideoAudioMuted ? <VolumeX className="w-3.5 h-3.5 text-rose-400" /> : <Volume2 className="w-3.5 h-3.5 text-slate-400" />}
              <span>{isVideoAudioMuted ? 'Muted 🔇' : 'Mute Video'}</span>
            </button>
          </div>

          {/* Play/Pause */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => setIsPlaying(!isPlaying)}
              className="w-7 h-7 rounded-lg bg-slate-800 hover:bg-slate-700 text-white flex items-center justify-center transition-colors cursor-pointer"
            >
              {isPlaying ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5 fill-white ml-0.5" />}
            </button>
            <div className="font-mono text-xs font-bold text-slate-200 tabular-nums">
              {formatDuration(playheadSec)} <span className="text-slate-500">/</span> {formatDuration(totalTimelineDuration)}
            </div>
          </div>

          {/* Zoom Slider */}
          <div className="flex items-center gap-2">
            <ZoomOut className="w-3.5 h-3.5 text-slate-400" />
            <input
              type="range"
              min="8"
              max="45"
              value={timelineZoom}
              onChange={(e) => setTimelineZoom(parseInt(e.target.value, 10))}
              className="w-20 accent-indigo-500 cursor-pointer h-1.5 bg-slate-700 rounded-lg"
              title="Zoom timeline scale"
            />
            <ZoomIn className="w-3.5 h-3.5 text-slate-400" />
          </div>
        </div>

        {/* ======================================================== */}
        {/* TOOLBAR 2: SELECTED OVERLAY MANUAL RESIZE & SCALE INSPECTOR */}
        {/* ======================================================== */}
        {selectedOverlay && (
          <div className="h-10 px-4 bg-purple-950/50 border-b border-purple-500/30 flex items-center justify-between text-xs text-purple-200 shrink-0 overflow-x-auto gap-4">
            <div className="flex items-center gap-2 font-bold shrink-0">
              <span className="w-2 h-2 rounded-full bg-purple-400 animate-ping" />
              <span className="text-white truncate max-w-[140px]">
                {selectedOverlay.type === 'text' ? '🔤 Text' : '🖼️ Image'}: {selectedOverlay.name}
              </span>
            </div>

            {/* MANUAL CANVAS SIZE / RESIZE CONTROLS */}
            <div className="flex items-center gap-3 shrink-0">
              {/* Preset Size Buttons */}
              <div className="flex items-center gap-1 bg-black/40 px-2 py-0.5 rounded-lg border border-purple-500/30 text-[10px]">
                <Scaling className="w-3 h-3 text-purple-300" />
                <span className="text-purple-300 font-bold mr-1">Resize:</span>
                {[
                  { label: '25%', w: 25, h: 18 },
                  { label: '50%', w: 50, h: 35 },
                  { label: '75%', w: 75, h: 55 },
                  { label: '100% Full', w: 100, h: 100, x: 0, y: 0 }
                ].map((sz) => (
                  <button
                    key={sz.label}
                    onClick={() => {
                      pushToHistory();
                      setOverlayClips(prev =>
                        prev.map(c =>
                          c.id === selectedOverlay.id
                            ? {
                                ...c,
                                widthPercent: sz.w,
                                heightPercent: sz.h,
                                xPercent: sz.x !== undefined ? sz.x : c.xPercent,
                                yPercent: sz.y !== undefined ? sz.y : c.yPercent,
                                position: 'custom'
                              }
                            : c
                        )
                      );
                      notifyAction(`✓ Resized to ${sz.label}!`);
                    }}
                    className="px-1.5 py-0.5 rounded bg-slate-800 hover:bg-purple-600 text-white font-mono font-bold cursor-pointer transition-colors"
                  >
                    {sz.label}
                  </button>
                ))}
              </div>

              {/* Direct Numeric Width% & Height% Inputs */}
              <div className="flex items-center gap-1.5 bg-black/40 px-2 py-0.5 rounded-lg border border-purple-500/30 text-[10px]">
                <span className="text-purple-300 font-bold">W:</span>
                <input
                  type="number"
                  min="5"
                  max="100"
                  value={Math.round(selectedOverlay.widthPercent ?? 26)}
                  onChange={(e) => {
                    const w = Math.max(5, Math.min(100, parseInt(e.target.value) || 26));
                    setOverlayClips(prev =>
                      prev.map(c =>
                        c.id === selectedOverlay.id
                          ? { ...c, widthPercent: w, position: 'custom' }
                          : c
                      )
                    );
                  }}
                  className="w-10 bg-slate-800 text-white border border-slate-700 rounded px-1 text-center font-bold font-mono"
                  title="Width in %"
                />
                <span className="text-purple-300 font-bold">H:</span>
                <input
                  type="number"
                  min="5"
                  max="100"
                  value={Math.round(selectedOverlay.heightPercent ?? 18)}
                  onChange={(e) => {
                    const h = Math.max(5, Math.min(100, parseInt(e.target.value) || 18));
                    setOverlayClips(prev =>
                      prev.map(c =>
                        c.id === selectedOverlay.id
                          ? { ...c, heightPercent: h, position: 'custom' }
                          : c
                      )
                    );
                  }}
                  className="w-10 bg-slate-800 text-white border border-slate-700 rounded px-1 text-center font-bold font-mono"
                  title="Height in %"
                />
                <span className="text-slate-400 font-mono">%</span>
              </div>

              {/* Slider Scale Increment */}
              <div className="flex items-center gap-1.5 bg-black/40 px-2 py-0.5 rounded-lg border border-purple-500/30">
                <button
                  onClick={() => {
                    pushToHistory();
                    setOverlayClips(prev =>
                      prev.map(c =>
                        c.id === selectedOverlay.id
                          ? {
                              ...c,
                              widthPercent: Math.max(10, (c.widthPercent || 26) - 5),
                              heightPercent: Math.max(10, (c.heightPercent || 18) - 4),
                              position: 'custom'
                            }
                          : c
                      )
                    );
                  }}
                  className="px-1 rounded bg-slate-800 hover:bg-slate-700 text-white font-mono font-bold"
                  title="Scale down 5%"
                >
                  -
                </button>
                <span className="font-mono font-bold text-white text-[11px] w-14 text-center">
                  {Math.round(selectedOverlay.widthPercent || 26)}% size
                </span>
                <button
                  onClick={() => {
                    pushToHistory();
                    setOverlayClips(prev =>
                      prev.map(c =>
                        c.id === selectedOverlay.id
                          ? {
                              ...c,
                              widthPercent: Math.min(100, (c.widthPercent || 26) + 5),
                              heightPercent: Math.min(100, (c.heightPercent || 18) + 4),
                              position: 'custom'
                            }
                          : c
                      )
                    );
                  }}
                  className="px-1 rounded bg-slate-800 hover:bg-slate-700 text-white font-mono font-bold"
                  title="Scale up 5%"
                >
                  +
                </button>
              </div>

              {/* Text Font Size & Color (If Text Overlay) */}
              {selectedOverlay.type === 'text' && (
                <div className="flex items-center gap-1.5 bg-black/40 px-2 py-0.5 rounded-lg border border-purple-500/30 text-[10px]">
                  <span className="text-purple-300 font-bold">Font:</span>
                  <button
                    onClick={() => {
                      pushToHistory();
                      setOverlayClips(prev =>
                        prev.map(c =>
                          c.id === selectedOverlay.id
                            ? { ...c, fontSize: Math.max(12, (c.fontSize || 24) - 2) }
                            : c
                        )
                      );
                    }}
                    className="px-1 rounded bg-slate-800 hover:bg-slate-700 text-white font-mono font-bold"
                    title="Smaller text"
                  >
                    -
                  </button>
                  <span className="font-mono font-bold text-white text-[10px] w-8 text-center">
                    {selectedOverlay.fontSize || 24}px
                  </span>
                  <button
                    onClick={() => {
                      pushToHistory();
                      setOverlayClips(prev =>
                        prev.map(c =>
                          c.id === selectedOverlay.id
                            ? { ...c, fontSize: Math.min(96, (c.fontSize || 24) + 2) }
                            : c
                        )
                      );
                    }}
                    className="px-1 rounded bg-slate-800 hover:bg-slate-700 text-white font-mono font-bold"
                    title="Larger text"
                  >
                    +
                  </button>
                  <input
                    type="color"
                    value={selectedOverlay.textColor || '#ffffff'}
                    onChange={(e) => {
                      const col = e.target.value;
                      setOverlayClips(prev =>
                        prev.map(c =>
                          c.id === selectedOverlay.id
                            ? { ...c, textColor: col }
                            : c
                        )
                      );
                    }}
                    className="w-4 h-4 rounded cursor-pointer border border-slate-600 bg-transparent"
                    title="Text Color"
                  />
                </div>
              )}

              {/* Position selector */}
              <div className="flex items-center gap-1 text-[10px]">
                <select
                  value={selectedOverlay.position || 'top-right'}
                  onChange={(e) => {
                    pushToHistory();
                    const pos = e.target.value as any;
                    setOverlayClips(prev =>
                      prev.map(c => (c.id === selectedOverlay.id ? { ...c, position: pos } : c))
                    );
                  }}
                  className="bg-slate-800 border border-slate-700 text-white rounded px-1.5 py-1 font-bold cursor-pointer"
                >
                  <option value="top-right">Top-Right (Watermark)</option>
                  <option value="top-left">Top-Left</option>
                  <option value="bottom-right">Bottom-Right</option>
                  <option value="bottom-left">Bottom-Left</option>
                  <option value="center">Center (Slide)</option>
                  <option value="pip">PIP (Window)</option>
                  <option value="custom">Custom Position</option>
                </select>
              </div>

              {/* Start & Duration Inputs */}
              <div className="flex items-center gap-2 text-[10px] font-mono">
                <span>Start:</span>
                <input
                  type="number"
                  step="0.5"
                  min="0"
                  value={selectedOverlay.timelineStartSec}
                  onChange={(e) => {
                    const v = parseFloat(e.target.value) || 0;
                    setOverlayClips(prev =>
                      prev.map(c => (c.id === selectedOverlay.id ? { ...c, timelineStartSec: Math.max(0, v) } : c))
                    );
                  }}
                  className="w-12 bg-slate-800 text-white border border-slate-700 rounded px-1 text-center font-bold"
                />
                <span>Dur:</span>
                <input
                  type="number"
                  step="0.5"
                  min="0.5"
                  value={selectedOverlay.durationSec}
                  onChange={(e) => {
                    const v = parseFloat(e.target.value) || 1;
                    setOverlayClips(prev =>
                      prev.map(c => (c.id === selectedOverlay.id ? { ...c, durationSec: Math.max(0.5, v) } : c))
                    );
                  }}
                  className="w-12 bg-slate-800 text-white border border-slate-700 rounded px-1 text-center font-bold"
                />
              </div>

              <button
                onClick={handleDeleteSelectedClip}
                className="text-rose-400 hover:text-rose-300 flex items-center gap-1 font-bold cursor-pointer"
                title="Delete this overlay"
              >
                <Trash2 className="w-3 h-3" />
                <span>Remove</span>
              </button>
            </div>
          </div>
        )}

        {/* TIMELINE LANES CONTAINER */}
        <div
          ref={timelineContainerRef}
          onClick={handleTimelineScrub}
          onDragOver={handleTimelineDragOver}
          onDragLeave={handleTimelineDragLeave}
          onDrop={handleTimelineDrop}
          className="flex-1 overflow-x-auto overflow-y-auto relative bg-slate-950 p-2 cursor-pointer select-none"
        >
          <div
            style={{ width: `${Math.max(900, totalTimelineDuration * timelineZoom + 200)}px` }}
            className="relative min-h-full flex flex-col justify-start gap-2.5 pb-4"
          >
            {/* 1. TIME RULER */}
            <div className="h-6 border-b border-slate-800 flex items-center relative text-[9px] font-mono text-slate-500 pointer-events-none mb-1">
              {Array.from({ length: Math.ceil(totalTimelineDuration) + 1 }).map((_, sec) => {
                const posX = sec * timelineZoom;
                const isMajor = sec % 5 === 0;
                return (
                  <div key={sec} style={{ left: `${posX}px` }} className="absolute bottom-0 flex flex-col items-start">
                    <span className="w-px h-2 bg-slate-700" />
                    {isMajor && <span className="pl-1 -mt-3">{formatDuration(sec)}</span>}
                  </div>
                );
              })}
            </div>

            {/* DRAG-AND-DROP VERTICAL GUIDE NEEDLE */}
            {timelineDropGuideSec !== null && (
              <div
                style={{ left: `${timelineDropGuideSec * timelineZoom}px` }}
                className="absolute top-0 bottom-0 w-0.5 bg-purple-400 border-l-2 border-dashed border-purple-400 pointer-events-none z-40 shadow-[0_0_12px_rgba(168,85,247,1)]"
              >
                <div className="absolute -top-5 -translate-x-1/2 px-2 py-0.5 rounded bg-purple-600 text-white text-[9px] font-mono font-bold shadow-lg whitespace-nowrap">
                  + Drop to place at {formatDuration(timelineDropGuideSec)}
                </div>
              </div>
            )}

            {/* 2. TRACK LANE: OVERLAY & IMAGE TRACK */}
            <div className="rounded-xl bg-slate-900/60 border border-slate-800/80 p-1.5">
              <div className="flex items-center justify-between text-[10px] text-slate-400 px-1 mb-1">
                <span className="font-bold flex items-center gap-1 text-purple-300">
                  <Layers className="w-3.5 h-3.5 text-purple-400" />
                  OVERLAY & IMAGE TRACK ({overlayClips.length} clips)
                </span>
                <span className="text-[9px] text-purple-400/80 font-mono">
                  Drag body to move • Drag purple [ | ] edge handles to adjust time • Resize on canvas
                </span>
              </div>

              <div className="h-16 bg-slate-950/80 rounded-lg p-1 relative flex items-center">
                {overlayClips.length === 0 ? (
                  <div className="text-[10px] text-slate-500 italic pl-2">
                    Drop images here or click "+ Add to Timeline" in Media tab to place here.
                  </div>
                ) : (
                  overlayClips.map((ovl) => {
                    const leftPx = ovl.timelineStartSec * timelineZoom;
                    const widthPx = Math.max(36, ovl.durationSec * timelineZoom);
                    const isSelected = selectedOverlayClipId === ovl.id;

                    return (
                      <div
                        key={ovl.id}
                        style={{ left: `${leftPx}px`, width: `${widthPx}px` }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedOverlayClipId(ovl.id);
                          setSelectedClipId(null);
                          setSelectedMusicClipId(null);
                          setSelectedAudioClipId(null);
                          setPlayheadSec(ovl.timelineStartSec);
                        }}
                        onMouseDown={(e) =>
                          handleStartTimelineDrag(e, 'move', 'overlay', ovl.id, ovl.timelineStartSec, ovl.durationSec)
                        }
                        className={`absolute top-1 bottom-1 rounded-xl flex items-center justify-between transition-shadow border-2 cursor-grab active:cursor-grabbing select-none group/clip ${
                          isSelected
                            ? 'border-purple-400 ring-2 ring-purple-400/40 shadow-[0_0_15px_rgba(168,85,247,0.4)] z-20'
                            : 'border-purple-600/70 hover:border-purple-500/90 z-10'
                        }`}
                      >
                        {/* 1. FLOATING TITLE BADGE ON TOP */}
                        <div className="absolute -top-5 left-1 px-1.5 py-0.5 rounded bg-white text-slate-950 border border-slate-300 text-[9px] font-mono font-bold shadow pointer-events-none truncate max-w-[200px] z-30">
                          {ovl.name}
                        </div>

                        {/* 2. LEFT RESIZE HANDLE */}
                        <div
                          onMouseDown={(e) =>
                            handleStartTimelineDrag(e, 'resize-left', 'overlay', ovl.id, ovl.timelineStartSec, ovl.durationSec)
                          }
                          className="absolute left-0 top-0 bottom-0 w-3.5 bg-purple-600 hover:bg-purple-500 rounded-l-xl flex items-center justify-center cursor-ew-resize z-30 shadow-md transition-colors"
                          title="Drag left edge to adjust start time"
                        >
                          <div className="w-0.5 h-4 bg-white rounded-full shadow-sm pointer-events-none" />
                        </div>

                        {/* 3. BODY: FILMSTRIP THUMBNAILS OR TEXT BANNER */}
                        <div className="w-full h-full flex overflow-hidden rounded-xl bg-purple-950/40 pointer-events-none">
                          {ovl.type === 'text' ? (
                            <div className="w-full h-full flex items-center justify-center font-bold text-[10px] text-purple-200">
                              🔤 {ovl.text || 'Text Title'}
                            </div>
                          ) : (
                            Array.from({ length: Math.max(1, Math.ceil(widthPx / 45)) }).map((_, idx) => (
                              <img
                                key={idx}
                                src={ovl.url}
                                alt=""
                                className="w-[45px] h-full object-cover shrink-0 border-r border-black/40 opacity-95"
                              />
                            ))
                          )}
                        </div>

                        {/* 4. RIGHT RESIZE HANDLE */}
                        <div
                          onMouseDown={(e) =>
                            handleStartTimelineDrag(e, 'resize-right', 'overlay', ovl.id, ovl.timelineStartSec, ovl.durationSec)
                          }
                          className="absolute right-0 top-0 bottom-0 w-3.5 bg-purple-600 hover:bg-purple-500 rounded-r-xl flex items-center justify-center cursor-ew-resize z-30 shadow-md transition-colors"
                          title="Drag right edge to stretch or shrink duration"
                        >
                          <div className="w-0.5 h-4 bg-white rounded-full shadow-sm pointer-events-none" />
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* 3. TRACK LANE: MAIN VIDEO TRACK */}
            <div className="rounded-xl bg-slate-900/60 border border-slate-800/80 p-1.5">
              <div className="flex items-center justify-between text-[10px] text-slate-400 px-1 mb-1">
                <span className="font-bold flex items-center gap-1 text-indigo-300">
                  <Film className="w-3.5 h-3.5 text-indigo-400" />
                  MAIN VIDEO TRACK ({timelineClips.length} clips)
                </span>
                <span className="text-[9px] text-indigo-400/80 font-mono">
                  Drag body to move • Drag blue [ | ] edge handles to trim
                </span>
              </div>

              <div className="h-16 flex items-center gap-1 relative bg-slate-950/80 rounded-lg p-1">
                {timelineClips.map((c) => {
                  const leftPx = c.timelineStartSec * timelineZoom;
                  const widthPx = Math.max(36, c.durationSec * timelineZoom);
                  const isSelected = selectedClipId === c.id;

                  return (
                    <div
                      key={c.id}
                      style={{ left: `${leftPx}px`, width: `${widthPx}px` }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedClipId(c.id);
                        setSelectedOverlayClipId(null);
                        setSelectedMusicClipId(null);
                        setSelectedAudioClipId(null);
                      }}
                      onMouseDown={(e) =>
                        handleStartTimelineDrag(e, 'move', 'video', c.id, c.timelineStartSec, c.durationSec)
                      }
                      className={`absolute top-1 bottom-1 rounded-xl flex items-center justify-between border-2 cursor-grab active:cursor-grabbing select-none transition-shadow ${
                        isSelected
                          ? 'border-indigo-400 ring-2 ring-indigo-400/40 shadow-[0_0_15px_rgba(99,102,241,0.4)] z-20'
                          : 'border-indigo-600/70 hover:border-indigo-500/90 z-10'
                      }`}
                    >
                      {/* Left Resize Handle */}
                      <div
                        onMouseDown={(e) =>
                          handleStartTimelineDrag(e, 'resize-left', 'video', c.id, c.timelineStartSec, c.durationSec)
                        }
                        className="absolute left-0 top-0 bottom-0 w-3.5 bg-indigo-600 hover:bg-indigo-500 rounded-l-xl flex items-center justify-center cursor-ew-resize z-30 shadow-md"
                        title="Drag left edge to trim"
                      >
                        <div className="w-0.5 h-4 bg-white rounded-full shadow-sm pointer-events-none" />
                      </div>

                      {/* Filmstrip Thumbnails */}
                      <div className="w-full h-full flex overflow-hidden rounded-xl bg-slate-900 pointer-events-none">
                        {Array.from({ length: Math.max(1, Math.ceil(widthPx / 40)) }).map((_, idx) => (
                          c.thumbnailUrl ? (
                            <img
                              key={idx}
                              src={c.thumbnailUrl}
                              alt=""
                              className="w-[40px] h-full object-cover shrink-0 border-r border-black/40 opacity-90"
                            />
                          ) : (
                            <div
                              key={idx}
                              className="w-[40px] h-full bg-indigo-900/60 shrink-0 border-r border-indigo-700/40 flex items-center justify-center"
                            >
                              <Film className="w-3.5 h-3.5 text-indigo-400" />
                            </div>
                          )
                        ))}
                      </div>

                      {/* Corner Volume Icon */}
                      <button
                        onClick={(e) => handleToggleClipMute(c.id, e)}
                        className={`absolute left-4 bottom-1.5 p-1 rounded transition-colors z-20 cursor-pointer ${
                          c.isMuted ? 'bg-rose-500 text-white' : 'bg-black/60 hover:bg-black/80 text-white'
                        }`}
                        title={c.isMuted ? 'Unmute this clip' : 'Mute this clip'}
                      >
                        {c.isMuted ? <VolumeX className="w-2.5 h-2.5" /> : <Volume2 className="w-2.5 h-2.5" />}
                      </button>

                      {/* Right Resize Handle */}
                      <div
                        onMouseDown={(e) =>
                          handleStartTimelineDrag(e, 'resize-right', 'video', c.id, c.timelineStartSec, c.durationSec)
                        }
                        className="absolute right-0 top-0 bottom-0 w-3.5 bg-indigo-600 hover:bg-indigo-500 rounded-r-xl flex items-center justify-center cursor-ew-resize z-30 shadow-md"
                        title="Drag right edge to trim"
                      >
                        <div className="w-0.5 h-4 bg-white rounded-full shadow-sm pointer-events-none" />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* 4. TRACK LANE: BACKGROUND MUSIC & AUDIO TRACK */}
            <div className="rounded-xl bg-slate-900/60 border border-slate-800/80 p-1.5">
              <div className="flex items-center justify-between text-[10px] text-slate-400 px-1 mb-1">
                <span className="font-bold flex items-center gap-1 text-teal-300">
                  <Music className="w-3.5 h-3.5 text-teal-400" />
                  BACKGROUND MUSIC & SFX TRACK ({musicClips.length} tracks)
                </span>
                <span className="text-[9px] text-teal-400/80 font-mono">
                  Drag body to move • Drag teal [ | ] edge handles to adjust length
                </span>
              </div>

              <div className="h-14 bg-slate-950/80 rounded-lg p-1 relative flex items-center">
                {musicClips.length === 0 ? (
                  <div className="text-[10px] text-slate-500 italic pl-2">
                    Drop audio files here or click "+ Add to Timeline" in Media tab to place here.
                  </div>
                ) : (
                  musicClips.map((mus) => {
                    const leftPx = mus.timelineStartSec * timelineZoom;
                    const widthPx = Math.max(36, mus.durationSec * timelineZoom);
                    const isSelected = selectedMusicClipId === mus.id;

                    return (
                      <div
                        key={mus.id}
                        style={{ left: `${leftPx}px`, width: `${widthPx}px` }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedMusicClipId(mus.id);
                          setSelectedClipId(null);
                          setSelectedOverlayClipId(null);
                          setSelectedAudioClipId(null);
                        }}
                        onMouseDown={(e) =>
                          handleStartTimelineDrag(e, 'move', 'music', mus.id, mus.timelineStartSec, mus.durationSec)
                        }
                        className={`absolute top-1 bottom-1 rounded-xl flex items-center justify-between border-2 cursor-grab active:cursor-grabbing select-none transition-shadow ${
                          isSelected
                            ? 'border-teal-400 ring-2 ring-teal-400/40 shadow-[0_0_15px_rgba(20,184,166,0.4)] z-20'
                            : 'border-teal-600/70 hover:border-teal-500/90 z-10'
                        }`}
                      >
                        {/* Left Handle */}
                        <div
                          onMouseDown={(e) =>
                            handleStartTimelineDrag(e, 'resize-left', 'music', mus.id, mus.timelineStartSec, mus.durationSec)
                          }
                          className="absolute left-0 top-0 bottom-0 w-3.5 bg-teal-600 hover:bg-teal-500 rounded-l-xl flex items-center justify-center cursor-ew-resize z-30 shadow-md"
                          title="Drag left edge to adjust audio start"
                        >
                          <div className="w-0.5 h-4 bg-white rounded-full shadow-sm pointer-events-none" />
                        </div>

                        {/* Title pill */}
                        <div className="absolute top-1 left-4 z-20 flex items-center gap-1 text-[10px] font-bold text-white truncate max-w-[150px] pointer-events-none">
                          <Music className="w-3 h-3 text-teal-300 shrink-0" />
                          <span className="truncate">{mus.name}</span>
                        </div>

                        {/* Waveform */}
                        <div className="w-full h-full flex items-end gap-0.5 px-4 pb-1 rounded-xl bg-teal-950/70 pointer-events-none overflow-hidden opacity-90">
                          {Array.from({ length: Math.min(60, Math.floor(widthPx / 3)) }).map((_, idx) => (
                            <div
                              key={idx}
                              style={{ height: `${20 + ((idx * 27) % 75)}%` }}
                              className="w-0.5 bg-teal-300 rounded-full shrink-0"
                            />
                          ))}
                        </div>

                        {/* Right Handle */}
                        <div
                          onMouseDown={(e) =>
                            handleStartTimelineDrag(e, 'resize-right', 'music', mus.id, mus.timelineStartSec, mus.durationSec)
                          }
                          className="absolute right-0 top-0 bottom-0 w-3.5 bg-teal-600 hover:bg-teal-500 rounded-r-xl flex items-center justify-center cursor-ew-resize z-30 shadow-md"
                          title="Drag right edge to extend or trim audio"
                        >
                          <div className="w-0.5 h-4 bg-white rounded-full shadow-sm pointer-events-none" />
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* 5. VERTICAL RED PLAYHEAD SCRUBBER */}
            <div
              style={{ left: `${playheadSec * timelineZoom}px` }}
              className="absolute top-0 bottom-0 w-0.5 bg-rose-500 pointer-events-none z-40 shadow-[0_0_10px_rgba(244,63,94,1)]"
            >
              <div className="absolute -top-1 -translate-x-1/2 w-3 h-3 bg-rose-500 rounded-sm rotate-45 shadow-md" />
            </div>
          </div>
        </div>
      </div>

      {/* ======================================================== */}
      {/* 4. CLIPCHAMP VIDEO COMPOSITING EXPORT MODAL */}
      {/* (Bakes all timeline overlays, cuts & audio into real video) */}
      {/* ======================================================== */}
      {showExportModal && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4 animate-in fade-in">
          <div className="bg-slate-900 border border-slate-750 rounded-3xl w-full max-w-xl shadow-2xl overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="px-6 py-4 bg-slate-850 border-b border-slate-800 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-gradient-to-tr from-indigo-500 to-purple-600 flex items-center justify-center text-white shadow-md">
                  <Download className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-base font-extrabold text-white flex items-center gap-2">
                    Export Video
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 font-mono">
                      Clipchamp Studio
                    </span>
                  </h3>
                  <p className="text-xs text-slate-400">
                    Bake in all timeline cuts, overlay images, custom text, and audio
                  </p>
                </div>
              </div>
              {!isExporting && (
                <button
                  onClick={() => setShowExportModal(false)}
                  className="w-8 h-8 rounded-lg bg-slate-800 hover:bg-slate-750 text-slate-400 hover:text-white flex items-center justify-center transition-colors cursor-pointer"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-5">
              {/* Composition Summary Badges */}
              <div className="grid grid-cols-3 gap-2.5">
                <div className="bg-slate-950/80 border border-slate-800 rounded-xl p-3 text-center">
                  <div className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Aspect Ratio</div>
                  <div className="text-xs font-extrabold text-indigo-300 mt-0.5">
                    {activeAspectRatio === '9:16' ? '9:16 Shorts/Reels' : activeAspectRatio === '1:1' ? '1:1 Square' : '16:9 Landscape'}
                  </div>
                </div>
                <div className="bg-slate-950/80 border border-slate-800 rounded-xl p-3 text-center">
                  <div className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Duration</div>
                  <div className="text-xs font-mono font-bold text-white mt-0.5">
                    {formatDuration(totalTimelineDuration)} ({totalTimelineDuration.toFixed(1)}s)
                  </div>
                </div>
                <div className="bg-slate-950/80 border border-slate-800 rounded-xl p-3 text-center">
                  <div className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Timeline Overlays</div>
                  <div className="text-xs font-bold text-purple-300 mt-0.5">
                    {overlayClips.length} element(s) active
                  </div>
                </div>
              </div>

              {/* Quality / Resolution Selector */}
              <div>
                <label className="block text-xs font-bold text-slate-300 mb-2">
                  Export Video Quality
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    disabled={isExporting}
                    onClick={() => setExportResolution('1080p')}
                    className={`p-3 rounded-2xl border text-left transition-all cursor-pointer ${
                      exportResolution === '1080p'
                        ? 'bg-indigo-600/20 border-indigo-500 text-white ring-2 ring-indigo-500/40 shadow-glow'
                        : 'bg-slate-850 border-slate-750 text-slate-300 hover:border-slate-700'
                    }`}
                  >
                    <div className="text-xs font-extrabold flex items-center justify-between">
                      <span>1080p (Full HD)</span>
                      <span className="text-[9px] px-1.5 py-0.2 rounded bg-indigo-500/30 text-indigo-200">Recommended</span>
                    </div>
                    <div className="text-[10px] text-slate-400 mt-1">Crisp high-definition for YouTube, Instagram & Reels</div>
                  </button>

                  <button
                    type="button"
                    disabled={isExporting}
                    onClick={() => setExportResolution('720p')}
                    className={`p-3 rounded-2xl border text-left transition-all cursor-pointer ${
                      exportResolution === '720p'
                        ? 'bg-indigo-600/20 border-indigo-500 text-white ring-2 ring-indigo-500/40 shadow-glow'
                        : 'bg-slate-850 border-slate-750 text-slate-300 hover:border-slate-700'
                    }`}
                  >
                    <div className="text-xs font-extrabold flex items-center justify-between">
                      <span>720p (HD)</span>
                      <span className="text-[9px] px-1.5 py-0.2 rounded bg-slate-700 text-slate-300">Fast Export</span>
                    </div>
                    <div className="text-[10px] text-slate-400 mt-1">Smaller file size, fast encoding & easy sharing</div>
                  </button>
                </div>
              </div>

              {/* Live Preview & Progress Area during Export */}
              {isExporting && (
                <div className="bg-slate-950/90 border border-indigo-500/30 rounded-2xl p-4 space-y-3 animate-in fade-in">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-bold text-indigo-300 flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-indigo-400 animate-ping" />
                      {exportStatusText}
                    </span>
                    <span className="font-mono font-extrabold text-white text-sm">
                      {exportProgress}%
                    </span>
                  </div>

                  {/* Progress Bar */}
                  <div className="h-3 bg-slate-800 rounded-full overflow-hidden p-0.5 border border-slate-700">
                    <div
                      style={{ width: `${exportProgress}%` }}
                      className="h-full bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 rounded-full transition-all duration-300 shadow-glow"
                    />
                  </div>

                  {/* Live Rendered Frame Thumbnail */}
                  {exportPreviewUrl && (
                    <div className="flex items-center gap-3 pt-1">
                      <img
                        src={exportPreviewUrl}
                        alt="Preview"
                        className="w-20 h-14 rounded-lg object-contain bg-black border border-slate-800 shadow"
                      />
                      <div className="text-[11px] text-slate-400">
                        <span className="text-white font-semibold">Live Compositing Preview:</span> All image stickers, text, and frames are actively being rendered into the final video stream.
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Completed State */}
              {exportedBlobUrl && !isExporting && (
                <div className="bg-emerald-950/40 border border-emerald-500/40 rounded-2xl p-4 flex items-center gap-3 animate-in fade-in">
                  <CheckCircle2 className="w-8 h-8 text-emerald-400 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-bold text-emerald-200">
                      Export Complete! Edited video downloaded successfully.
                    </div>
                    <div className="text-[11px] text-slate-400 mt-0.5">
                      All timeline overlays, text, and cuts have been permanently baked in.
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 bg-slate-850 border-t border-slate-800 flex items-center justify-between">
              <div>
                {isExporting ? (
                  <button
                    type="button"
                    onClick={handleCancelExport}
                    className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-rose-900/40 hover:text-rose-300 border border-slate-700 text-xs font-bold text-slate-300 transition-colors cursor-pointer"
                  >
                    Cancel Export
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => setShowExportModal(false)}
                    className="px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-750 text-xs font-bold text-slate-300 transition-colors cursor-pointer"
                  >
                    Close
                  </button>
                )}
              </div>

              <div className="flex items-center gap-3">
                {exportedBlobUrl && (
                  <a
                    href={exportedBlobUrl}
                    download={`${currentSession.title.replace(/[^a-zA-Z0-9_-]/g, '_')}_Edited.mp4`}
                    className="flex items-center gap-1.5 px-4 py-2 rounded-xl bg-slate-800 hover:bg-slate-750 text-xs font-bold text-indigo-300 border border-indigo-500/40 transition-colors cursor-pointer"
                  >
                    <Download className="w-4 h-4" />
                    <span>Download Again</span>
                  </a>
                )}

                {!isExporting && (
                  <button
                    type="button"
                    onClick={handleStartCompositedExport}
                    className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-gradient-to-r from-indigo-600 via-purple-600 to-indigo-600 hover:from-indigo-500 hover:to-purple-500 text-white text-xs font-extrabold shadow-lg shadow-indigo-600/30 transition-all cursor-pointer active:scale-95"
                  >
                    <Download className="w-4 h-4" />
                    <span>Start Video Export ({exportResolution})</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Re-export as ParticipantReviewScreen
export const ParticipantReviewScreen = VideoEditorScreen;
